// src/pages/IssueDetailPage.tsx — SCE Issues
// Détail d'un problème : infos structurées, timeline d'activité, actions.
//
// ─── Pièces jointes ───
//
// Chargées une fois via listPiecesJointes(issueId), puis rangées par
// utils/rattachementPhotos : photos du problème (déclaration) ou photos d'un
// événement de l'activité. Toutes sont réunies dans le bloc Photos sous la
// description ; celles d'un événement restent aussi sous lui dans l'activité.
//
// ─── Lot 3.5 — Qualification par le coordinateur ───
// Bandeau de qualification inline pour le coordinateur du métier.
//
// ─── Lot 6 — Édition par le coordinateur ───
// Bouton « Modifier » en tête de fiche → formulaire unique (EditionProbleme),
// un événement « Modification » par enregistrement. Règles dans
// utils/modificationIssue.ts.

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type JSX, type ReactNode } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Send,
  CheckCircle2,
  Tag,
  Ban,
  PenLine,
  Paperclip,
  ChevronRight,
  Package,
  FolderOpen,
  Plus,
  UserRound,
  X,
  Loader2,
  RotateCcw,
  FileSpreadsheet,
  ExternalLink,
  History,
  Pencil,
} from 'lucide-react'
import { AppShell } from '@/components/layout/AppShell'
import { useDansModale, useMargeBoutonsModale, useOuvrirFiche, useVoletModale, type EtatModale } from '@/components/layout/ModaleRoute'
import { PastilleModifie } from '@/components/issues/PastilleModifie'
import { champsModifies, type CibleModification } from '@/utils/champsModifies'
import { useMaintenant } from '@/hooks/useMaintenant'
import { useHauteurAuto } from '@/hooks/useHauteurAuto'
import { PastilleEvenement } from '@/components/issues/PastilleEvenement'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { usePresence } from '@/components/presence/PresenceProvider'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/Button'
import { BoutonRafraichir } from '@/components/ui/BoutonRafraichir'
import { BoutonSuivre } from '@/components/issues/BoutonSuivre'
import { useRetour } from '@/hooks/useRetour'
import { SectionDocumentation } from '@/components/issues/SectionDocumentation'
import { DeclarationNcs } from '@/components/issues/DeclarationNcs'
import { EnvoiBE } from '@/components/issues/EnvoiBE'
import {
  BoutonEnvoiBE,
  DETAIL_ENVOI_BE,
  DETAIL_ENVOI_BE_EN_COURS,
  texteDernierEnvoi,
  titreEnvoiBE,
} from '@/components/issues/BoutonEnvoiBE'
import { MenuActionsFiche } from '@/components/issues/MenuActionsFiche'
import { envoiBEOuvert as envoiBEAccessible } from '@/config/be.config'
import { dernierEnvoi } from '@/utils/envoiBE'
import { lireBrouillonNcs } from '@/utils/brouillonNcs'
import { BadgeNcs } from '@/components/issues/BadgeNcs'
import { useReferenceNcs, type DeclarationAChercher } from '@/hooks/useReferenceNcs'
import type { Candidat } from '@/utils/registreNcs'
import { useVueRole } from '@/features/vue-role/contexte'
import type { ResultatDeclarationNcs } from '@/api/declarationNcs.api'
import {
  CRITICITE_PAR_PRIORITE,
  LIBELLE_EVENEMENT_NCS,
  PRIORITES,
  lireEvenementNcs,
  texteEvenementNcs,
  type PrioriteNcs,
} from '@/utils/declarationNcs'
import { CarteGroupe, CaseGroupe, LienAutreMachine } from '@/components/issues/CarteGroupe'
import { NavigationSuite } from '@/components/issues/NavigationSuite'
import { useIssues } from '@/hooks/useIssues'
import { ajouterMachine, appliquerAuxMembres } from '@/api/groupes.api'
import {
  BILAN_VIDE,
  eligibleCloture,
  eligibleQualification,
  libelleMachines,
  membresDuGroupe,
  porteeAChoisir,
  porteeSurLeGroupe,
  resumerBilan,
  texteRepublie,
  type BilanGroupe,
  type PorteeGroupe,
} from '@/utils/groupes'
import { LigneActionCarte, LigneSuivi, STYLE_CARTE, STYLE_TITRE_CARTE } from '@/components/issues/CarteFiche'
import { Badge } from '@/components/ui/Badge'
import { CriticiteLibellee, StatutLibelle } from '@/components/ui/EtatProbleme'
import { lienChefProjet, lienEquipement, lienFnct, lienProjet } from '@/utils/liensFiches'
import { FNCT_EN_ESSAI, FNCT_MASQUEE } from '@/config/fnct.config'
import { accesFnct } from '@/utils/droitsFnct'

import { useSnackbar } from '@/components/ui/Snackbar'
import {
  ajouterBadgeNcs,
  lierFnct,
  annulerIssue,
  enregistrerReferenceNcs,
  cloturerIssue,
  getIssueById,
  historiqueProbleme,
  modifierIssue,
  preparerTraitementQualite,
  qualifyIssue,
  rouvrirIssue,
  renseignerReferences,
} from '@/api/issues.api'
import { Bulle, GroupeBulles } from '@/components/ui/Bulle'
import { ChampReference, type SaisieReference } from '@/components/issues/ChampReference'
import {
  ChoixTraitementQualite,
  composerReferences,
  saisiesDepuis,
  type SaisiesReferences,
} from '@/components/issues/ChoixTraitementQualite'
import { ChoixCriticite, ChoixFamille } from '@/components/issues/ChoixQualification'
import { EditionProbleme } from '@/components/issues/EditionProbleme'
import {
  resumerModification,
  resumerReouverture,
  statutReouverture,
  type SaisieModification,
} from '@/utils/modificationIssue'
import { useConfirmerEcriture } from '@/features/vue-role/contexte'
import { getEquipementByNumeroSerie } from '@/api/equipements.api'
import { getCommentaires, addCommentaire, historiqueCommentaire, modifierCommentaire } from '@/api/commentaires.api'
import { BlocHistorique, BoutonHistorique } from '@/components/issues/HistoriqueChamp'
import { changementsAnnonces, historiqueDesChamps, LIBELLES_HISTORIQUE, type ChampHistorise, type HistoriqueProbleme } from '@/utils/historiqueChamps'
import { commentairesOuverts, erreurCorrection, libelleCorrection, peutModifierCommentaire, type VersionCommentaire } from '@/utils/editionCommentaire'
import { identitesDeclarant } from '@/utils/identiteDeclarant'
import { envoyerFichiers, lireContenu, listPiecesJointes, uploadFiles } from '@/api/files.api'
import { PieceJointeGallery } from '@/components/files/PieceJointeGallery'
import { BlocPhotos } from '@/components/files/BlocPhotos'
import { Visionneuse } from '@/components/files/Visionneuse'
import { EditeurAnnotation } from '@/components/files/EditeurAnnotation'
import { PieceJointePicker, preparerFichiers, type FichierEnAttente } from '@/components/files/PieceJointePicker'
import { fichiersColles } from '@/utils/pressePapier'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { signalerProblemesModifies } from '@/hooks/signalProblemesModifies'
import { METIERS_LABELS } from '@/config/metiers.config'
import { coordinateursDuProbleme, famillesDuProbleme, piloteLeProbleme } from '@/config/sujets.config'
import { estAdmin } from '@/config/admin.config'
import {
  A_QUALIFIER,
  ANNULE,
  BADGES_AVEC_REFERENCE,
  MOTIFS_ANNULATION,
  commentaireClotureObligatoire,
  composerReference,
  decomposerReference,
  estActif,
  estTermine,
  libelleBadge,
  ordonnerBadges,
  type BadgeAvecReference,
  type BadgeCloture,
  type Commentaire,
  type CriticiteTranchee,
  type Equipement,
  type Issue,
  type MotifAnnulation,
} from '@/types/issue.types'
import type { PieceJointe } from '@/api/files.api'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'
import { ZoneMentions } from '@/components/mentions/ZoneMentions'
import { TexteAvecMentions } from '@/components/mentions/TexteAvecMentions'
import { notifierMentions } from '@/api/mentions.api'
import { resumeMentions, type GesteMention, type PersonneMentionnee } from '@/utils/mentions'
import { composerPhotosFiche, nommerAnnotation, rattacherPhotos, type PhotoFiche } from '@/utils/rattachementPhotos'

// Escalade en FNC-T (lot F2) : réservée à l'administrateur pendant la
// construction, chargée seulement à l'ouverture — pas dans le paquet de tous.
const OuvertureFnct = lazy(() => import('@/components/fnct/OuvertureFnct').then((m) => ({ default: m.OuvertureFnct })))
const ReleveEcart = lazy(() => import('@/components/fnct/ReleveEcart').then((m) => ({ default: m.ReleveEcart })))
const LigneFnctProbleme = lazy(() => import('@/components/fnct/LigneFnctProbleme').then((m) => ({ default: m.LigneFnctProbleme })))

// ─── Constantes ───

/** Année pré-remplie dans les références qualité (FNC-T_<année>-…). */
const ANNEE_COURANTE = String(new Date().getFullYear())

// ─── Composant principal ───

// ─── Une fiche par problème ───
//
// Passer d'un problème à l'autre (« Problème suivant », fiche sœur d'un
// groupe) garde la même route, donc le même composant monté : sans la clé,
// les saisies d'une fiche — commentaire, bulles de qualification, formulaire
// de clôture — se retrouvaient sur la suivante. La fiche est remontée pour
// chaque problème ; la liste complète, elle, est lue ici et reste d'une fiche
// à l'autre.
export function IssueDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>()
  const dansModale = useDansModale()
  // Toutes les fiches : sœurs du groupe et révision EDE pour la fiche (Lot 14),
  // suite par défaut et aperçus pour « Problème suivant ».
  const { issues: tous, refreshIssues: relireTous } = useIssues()
  return (
    <>
      <FicheProbleme key={id} id={id} tous={tous} relireTous={relireTous} />
      {dansModale && <NavigationSuite idCourant={id} tous={tous} />}
    </>
  )
}

function FicheProbleme({
  id,
  tous,
  relireTous,
}: {
  id: string
  tous: Issue[]
  relireTous: () => Promise<void>
}): JSX.Element {
  // Lien de mail / Teams : pas de page précédente dans l'appli → la liste.
  const retour = useRetour('/issues')
  const { show } = useSnackbar()
  const { user, isFournisseur, nomReel, enVueRole } = useCurrentUser()

  const [issue, setIssue] = useState<Issue | null>(null)
  const [itemId, setItemId] = useState<string | null>(null)
  const [commentaires, setCommentaires] = useState<Commentaire[]>([])
  const [piecesJointes, setPiecesJointes] = useState<PieceJointe[]>([])
  const [pjLoading, setPjLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [nouveauCommentaire, setNouveauCommentaire] = useState('')
  const refZoneCommentaire = useHauteurAuto(nouveauCommentaire)
  const [commentaireCloture, setCommentaireCloture] = useState('')
  const [badgesCloture, setBadgesCloture] = useState<BadgeCloture[]>([])
  // Références qualité saisies à la clôture (Lot 3 bis) — facultatives.
  // Année pré-remplie : c'est presque toujours l'année en cours.
  const [refsSaisies, setRefsSaisies] = useState<SaisiesReferences>(() => saisiesDepuis())
  // Référence renseignée APRÈS la clôture : quel badge est en cours
  // d'édition, et la saisie en cours.
  const [refEnEdition, setRefEnEdition] = useState<BadgeAvecReference | null>(null)
  const [saisieEdition, setSaisieEdition] = useState<SaisieReference>({
    annee: ANNEE_COURANTE,
    numero: '',
  })
  const [submitting, setSubmitting] = useState(false)
  // Fiche v2 : clôture repliée derrière un bouton ; N° de plan en édition
  // (null = pas en édition, sinon la saisie en cours, un plan par ligne).
  const [clotureOuverte, setClotureOuverte] = useState(false)
  // Clôture (30/09) : le traitement qualité déjà enregistré s'affiche en
  // résumé ; « Modifier » rouvre ses bulles dans le formulaire de clôture.
  const [clotureChangeTraitement, setClotureChangeTraitement] = useState(false)
  // Carte « Traitement qualité » en édition (30/09) : sa propre saisie,
  // indépendante de celle de la clôture.
  const [tqOuvert, setTqOuvert] = useState(false)
  const [tqBadges, setTqBadges] = useState<BadgeCloture[]>([])
  const [tqSaisies, setTqSaisies] = useState<SaisiesReferences>(() => saisiesDepuis())
  const [tqNote, setTqNote] = useState('')
  // Lot 6 : formulaire d'édition ouvert (titre, description, métier…).
  const [edition, setEdition] = useState(false)
  // Lot 7 : réouverture par l'administrateur — null = fermée, sinon le
  // commentaire en cours de saisie.
  const [reouverture, setReouverture] = useState<string | null>(null)
  const [ncsOuverte, setNcsOuverte] = useState(false)
  // « Envoyer au BE » (20/10) : coordinateurs du métier, suppléants, admin.
  const [envoiBEOuvert, setEnvoiBEOuvert] = useState(false)
  // Escalade en FNC-T (chantier FNC-T, lot F2) : administrateur pendant la construction.
  // Une seule entrée « Ouvrir une FNC-T » (14/10) : le formulaire d'escalade, ou le
  // relevé d'écarts P&ID / PPL quand ce type y est choisi ; `modele` : ouverture
  // immédiate avec le modèle P&ID / PPL (machine sans NVS lisible, pas de relevé).
  const [fenetreFnct, setFenetreFnct] = useState<'escalade' | 'modele' | 'releve' | null>(null)
  // Relecture de la ligne FNC-T après un écart relevé (14/10).
  const [versionFnct, setVersionFnct] = useState(0)
  // Bascule de rôle (Lot 7) : confirmation avant toute écriture d'état,
  // puisqu'elle sera signée du compte réel et non de la personne affichée.
  const confirmerEcriture = useConfirmerEcriture()

  // PJ sur commentaire
  const [showPicker, setShowPicker] = useState(false)
  const [fichiersCommentaire, setFichiersCommentaire] = useState<FichierEnAttente[]>([])

  // Qualification
  const [qualifFamille, setQualifFamille] = useState('')
  const [qualifCriticite, setQualifCriticite] = useState<CriticiteTranchee | null>(null)
  const [qualifying, setQualifying] = useState(false)

  // Annulation (Lot 2) — même bandeau que la qualification, deux issues
  // possibles : qualifier OU annuler.
  const [modeAnnulation, setModeAnnulation] = useState(false)
  const [motifAnnulation, setMotifAnnulation] = useState<MotifAnnulation | null>(null)
  const [commentaireAnnulation, setCommentaireAnnulation] = useState('')

  // ─── Multi-équipements (Lot 14, option C) ───
  //
  // Toutes les fiches (`tous`, lues par IssueDetailPage) : les sœurs du
  // groupe (carte « Même défaut », cases « aussi sur les autres machines »)
  // et, pour la Documentation, la révision EDE la plus récente.
  // Qualifier, annuler, clore : tout le groupe par défaut, décochable
  // (réponse des coordinateurs). Commenter : au choix, décoché.
  const [appliquerGroupe, setAppliquerGroupe] = useState(true)
  // Cochée d'office (03/11, retour d'expérience) : un commentaire sur un défaut
  // commun documente tout le groupe. La clôture, elle, demande (porteeCloture).
  const [commenterGroupe, setCommenterGroupe] = useState(true)
  // Clore (02/11) : plus de case cochée d'office, on DEMANDE « ce problème
  // seul ou tout le groupe » — null tant qu'on n'a pas répondu (utils/groupes).
  const [porteeCloture, setPorteeCloture] = useState<PorteeGroupe | null>(null)
  // Photos déposées une fois, sur la première fiche du groupe.
  const [pjGroupe, setPjGroupe] = useState<PieceJointe[]>([])

  // ─── Équipement rattaché (demande 11) ───
  //
  // Un numéro de série seul (« 4844-SC ») ne dit rien à qui ne connaît pas la
  // machine par cœur. On affiche donc le modèle et la désignation du
  // référentiel à côté.
  //
  // Aucune requête supplémentaire : `getEquipementByNumeroSerie` lit le cache
  // mémoire de equipements.api.ts, déjà chargé par la liste, le dashboard ou
  // le formulaire de déclaration.
  //
  // Trois états distincts, et il en FAUT trois :
  //   undefined → pas encore résolu   → on n'affiche rien
  //   null      → absent du référentiel → on le dit
  //   Equipement→ trouvé               → modèle + désignation
  // Avec deux états seulement, « absent du référentiel » clignotait sur chaque
  // ouverture de fiche, le temps de la résolution.
  const [equipement, setEquipement] = useState<Equipement | null | undefined>(
    undefined,
  )

  // ─── Chargement ───

  // `silencieux` : relecture sans squelette de chargement — la fiche reste
  // affichée pendant la requête (bouton « Rafraîchir », retour d'erreur).
  const refresh = useCallback(async (silencieux = false): Promise<void> => {
    if (!silencieux) setLoading(true)
    setError(null)
    try {
      const [res, c] = await Promise.all([
        getIssueById(id),
        getCommentaires(id),
      ])
      if (res) {
        setIssue(res.issue)
        setItemId(res.itemId)
      } else {
        setIssue(null)
        setItemId(null)
      }
      setCommentaires(c)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement')
    } finally {
      setLoading(false)
    }
  }, [id])

  // Chargement des PJ séparé (indépendant du refresh issue/commentaires)
  const refreshPJ = useCallback(async (): Promise<void> => {
    setPjLoading(true)
    try {
      const list = await listPiecesJointes(id)
      setPiecesJointes(list)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[IssueDetail] listPiecesJointes échoué:', err)
    } finally {
      setPjLoading(false)
    }
  }, [id])

  useEffect(() => {
    void refresh()
    void refreshPJ()
  }, [refresh, refreshPJ])

  // ─── Listes à recharger à la fermeture ───
  //
  // Sur bureau, la liste d'où l'on vient est restée montée derrière la fiche.
  // Si la fiche a écrit quelque chose, elle le signale en se refermant, et
  // les listes se rechargent en silence (cf. signalProblemesModifies). Sur
  // téléphone, la liste est remontée — donc rechargée — de toute façon.
  const aEcrit = useRef(false)
  useEffect(
    () => () => {
      if (aEcrit.current) signalerProblemesModifies()
    },
    [],
  )

  // ─── Rafraîchir à la demande ───
  //
  // Pas de temps réel (Static Web App, pas de backend) : un autre coordinateur
  // peut qualifier, commenter ou modifier pendant que la fiche est ouverte —
  // le bandeau de présence le signale. Ce bouton relit fiche, activité et
  // pièces jointes sans fermer la fiche.
  const [rafraichissement, setRafraichissement] = useState(false)
  const rafraichir = useCallback(async (): Promise<void> => {
    setRafraichissement(true)
    try {
      await Promise.all([refresh(true), refreshPJ()])
    } finally {
      setRafraichissement(false)
    }
  }, [refresh, refreshPJ])

  // Photos du groupe (Lot 14) : à la déclaration, elles sont déposées une
  // fois, dans le dossier de la première fiche. Les fiches sœurs les montrent
  // avec la description. Échec silencieux, comme pour l'équipement.
  const groupeIssue = issue?.groupe
  useEffect(() => {
    if (!groupeIssue || groupeIssue === id) {
      setPjGroupe([])
      return
    }
    let annule = false
    listPiecesJointes(groupeIssue)
      .then((liste) => {
        if (!annule) setPjGroupe(liste)
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn('[IssueDetail] photos du groupe non lues :', err)
      })
    return () => {
      annule = true
    }
  }, [groupeIssue, id])

  // Résolution de l'équipement rattaché.
  //
  // Échec volontairement silencieux : l'équipement est un ENRICHISSEMENT de la
  // fiche, pas sa raison d'être. Un référentiel injoignable, ou une machine
  // pas encore importée de Windchill, ne doit pas couvrir la fiche d'un
  // message d'erreur — le numéro de série reste affiché, seul.
  const numeroSerieIssue = issue?.numeroSerie
  useEffect(() => {
    if (!numeroSerieIssue) {
      setEquipement(undefined)
      return
    }
    let annule = false
    getEquipementByNumeroSerie(numeroSerieIssue)
      .then((eq) => {
        if (!annule) setEquipement(eq)
      })
      .catch((err: unknown) => {
        // Référentiel injoignable : on reste en « non résolu », on n'affirme
        // pas que l'équipement est absent — ce serait un mensonge.
        if (!annule) setEquipement(undefined)
        // eslint-disable-next-line no-console
        console.warn('[IssueDetail] équipement non résolu:', err)
      })
    return () => {
      annule = true
    }
  }, [numeroSerieIssue])

  // ─── Dérivés ───

  // Titulaire OU suppléant du métier (Lot 10 : Hugo, suppléant ELEC + AUTOM),
  // ou responsable du sujet (23/10 : Thibaut sur les goulottes, problèmes MECA).
  const estCoordinateurDuMetier = useMemo(() => {
    if (!issue || !user || isFournisseur) return false
    return piloteLeProbleme(user.upn, issue)
  }, [issue, user, isFournisseur])

  // ─── Qui pilote ce problème ───
  //
  // Qualifier, annuler, clore : réservé au coordinateur du métier, et à
  // l'administrateur. Avant le Lot 2, la clôture était ouverte à TOUT
  // non-fournisseur — un opérateur pouvait clore n'importe quel problème,
  // contrairement au tableau des rôles de claude.md.
  //
  // L'administrateur est inclus dès maintenant : il n'est coordinateur
  // d'aucun métier, et sans ça il ne pourrait ni tester ni débloquer ce lot
  // en production. Même règle que celle retenue pour l'édition (décision 6).
  // Administrateur sur l'identité AFFICHÉE : en bascule de rôle, on voit —
  // et on édite — exactement comme la personne affichée (Lot 7).
  const estAdminAffiche = !isFournisseur && (user ? estAdmin(user.upn) : false)
  const peutPiloter = useMemo(
    () => !isFournisseur && (estCoordinateurDuMetier || estAdminAffiche),
    [isFournisseur, estCoordinateurDuMetier, estAdminAffiche],
  )

  const familleAQualifier = issue?.famille === A_QUALIFIER
  const criticiteAQualifier = issue?.criticite === A_QUALIFIER
  const aQualifier = familleAQualifier || criticiteAQualifier

  const famillesDispo = useMemo(
    () => (issue ? famillesDuProbleme(issue) : []),
    [issue],
  )

  // ─── Coordinateur de la carte Suivi ───
  //
  // Celui qui a QUALIFIÉ le problème (ou l'a annulé) : l'auteur du dernier
  // événement de ce type dans l'activité, déjà chargée — aucune requête de
  // plus. La colonne Coordinateur (field_15) ne dit que qui est assigné par
  // métier, pas qui a tranché ; elle n'était d'ailleurs jamais relue.
  //
  // Tant que le problème attend sa qualification (y compris après un
  // changement de métier), on affiche qui doit la faire, en discret.
  const coordinateurSuivi = useMemo((): { nom: string; enAttente: boolean } | null => {
    if (!issue) return null
    if (estActif(issue) && aQualifier) {
      return { nom: coordinateursDuProbleme(issue).map((c) => c.nom).join(', '), enAttente: true }
    }
    const acte = [...commentaires]
      .reverse()
      .find((c) => c.type === 'Qualification' || c.type === 'Annulation')
    return acte?.auteur ? { nom: acte.auteur, enAttente: false } : null
  }, [issue, aQualifier, commentaires])

  // Les autres fiches du même défaut, et celles qu'une action peut suivre.
  const membres = useMemo(() => (issue ? membresDuGroupe(issue, tous) : []), [issue, tous])

  // ─── Criticité proposée d'après la NC-S (06/10) ───
  // Un problème parti en NC-S attend encore sa qualification (famille et
  // criticité nourrissent stats et export) ; la priorité choisie dans la NC-S
  // pré-coche la criticité : le coordinateur n'a plus qu'à valider.
  const prioriteNcs = useMemo(
    () => declarationsNcsDe(commentaires).filter((d) => d.priorite).at(-1)?.priorite,
    [commentaires],
  )
  const criticiteProposee = useRef(false)
  useEffect(() => {
    if (criticiteProposee.current || !prioriteNcs || issue?.statut !== 'Ouvert') return
    criticiteProposee.current = true
    setQualifCriticite((c) => c ?? CRITICITE_PAR_PRIORITE[prioriteNcs])
  }, [prioriteNcs, issue?.statut])

  // ─── N° de la NC-S, cherché dans le registre qualité (06/10) ───
  // Tant qu'une NC-S envoyée n'a pas son N°, la fiche le cherche à
  // l'ouverture (hooks/useReferenceNcs) et l'écrit s'il est sûr.
  const { profil: profilAffiche } = useVueRole()
  const ncsAChercher = useMemo((): DeclarationAChercher | null => {
    if (!issue || isFournisseur || issue.refNcs || issue.statut === 'Annulé') return null
    const d = declarationsNcsDe(commentaires).at(-1)
    return d ? { issueId: issue.id, fichier: d.fichier, date: d.date, auteur: d.auteur } : null
  }, [issue, isFournisseur, commentaires])
  const refsNcsPrises = useMemo(
    () => new Set(tous.flatMap((i) => (i.id !== id && i.refNcs ? [i.refNcs] : []))),
    [tous, id],
  )
  const ecrireRefNcs = useCallback(
    async (c: Candidat, auto: boolean): Promise<void> => {
      if (!itemId) return
      const { ecrit } = await enregistrerReferenceNcs(itemId, c.ligne.numero)
      // Déjà écrit par la recherche de fond (features/reference-ncs) : rien à tracer.
      if (!ecrit) {
        await refresh(true)
        return
      }
      await tracer(
        {
          problemId: id,
          type: 'Modification',
          contenu: `N° de NC-S ${auto ? 'trouvé' : 'retenu'} dans le registre qualité : ${c.ligne.numero}`,
        },
        `NC-S : ${c.ligne.numero}`,
      )
      await refresh(true)
    },
    // `tracer` est redéclarée à chaque rendu mais ne lit que des valeurs stables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemId, id, refresh],
  )
  const referenceNcs = useReferenceNcs({
    declaration: ncsAChercher,
    prises: refsNcsPrises,
    ecrire: ecrireRefNcs,
    lectureSeule: profilAffiche !== null,
  })
  const nbAQualifier = membres.filter(eligibleQualification).length
  const nbActifs = membres.filter(eligibleCloture).length

  // Rattachement PJ ↔ commentaires
  const pjAttribution = useMemo(
    () => rattacherPhotos(piecesJointes, commentaires),
    [piecesJointes, commentaires],
  )
  const photosFiche = useMemo(
    () => composerPhotosFiche(
      pjAttribution,
      commentaires,
      { auteur: issue?.declarantNom || issue?.declarant || '', date: issue?.dateDeclaration ?? '', upn: issue?.declarant },
      pjGroupe,
    ),
    [pjAttribution, commentaires, issue?.declarantNom, issue?.declarant, issue?.dateDeclaration, pjGroupe],
  )
  // Les commentaires qui portent une photo : « Envoyer au BE » les garde en priorité.
  const commentairesAvecPhoto = useMemo(
    () => new Set(pjAttribution.parCommentaire.keys()),
    [pjAttribution],
  )
  const [rangVisionneuse, setRangVisionneuse] = useState<number | null>(null)
  const ouvrirPhoto = useCallback((pj: PieceJointe): void => {
    // Une version annotée s'ouvre sur sa photo, qui la montre d'abord.
    const rang = photosFiche.findIndex((p) => p.pj.id === pj.id || p.annotation?.pj.id === pj.id)
    if (rang >= 0) setRangVisionneuse(rang)
  }, [photosFiche])
  // Annotation depuis la visionneuse : publiée comme un nouveau commentaire.
  const [aAnnoter, setAAnnoter] = useState<{ photo: PhotoFiche; affichee: PieceJointe } | null>(null)
  const [envoiAnnotation, setEnvoiAnnotation] = useState(false)
  // ─── Historique des corrections (23/10, lot 1) ───
  // Titre, description, phase, N° de plan, EDE : corrigés sans événement, leur
  // historique se lit dans les versions SharePoint (une requête, en fond, à
  // chaque nouvel état de la fiche). Échec : pas d'icône, rien d'autre.
  const [historique, setHistorique] = useState<HistoriqueProbleme | null>(null)
  const [champHistorique, setChampHistorique] = useState<ChampHistorise | null>(null)
  useEffect(() => {
    if (!itemId || !issue) return
    let annule = false
    historiqueProbleme(itemId)
      .then((v) => {
        if (!annule) setHistorique(historiqueDesChamps(v))
      })
      .catch((err) => console.warn('[IssueDetail] historique de la fiche illisible :', err))
    return () => {
      annule = true
    }
  }, [itemId, issue])
  const iconeHistorique = (champ: ChampHistorise): JSX.Element | null =>
    historique ? (
      <BoutonHistorique
        textes={historique[champ]}
        libelle={LIBELLES_HISTORIQUE[champ]}
        ouvert={champHistorique === champ}
        onBasculer={() => setChampHistorique((c) => (c === champ ? null : champ))}
      />
    ) : null
  const blocHistorique = (champ: ChampHistorise): JSX.Element | null =>
    historique && champHistorique === champ && historique[champ].length > 1 ? <BlocHistorique textes={historique[champ]} /> : null

  // ─── Corriger son commentaire (23/10) ───
  // Ses propres commentaires seulement, là où l'on peut commenter (clos compris
  // depuis le 26/10, jamais un annulé). Pas en bascule de rôle : l'écriture
  // partirait sous le compte réel, pas celui affiché.
  const mesIdentites = useMemo(() => (user ? identitesDeclarant(user) : []), [user])
  const ouvertAuxCommentaires = !!issue && commentairesOuverts(issue.statut)
  const peutCorriger = useCallback(
    (c: Commentaire): boolean => !enVueRole && peutModifierCommentaire(c, mesIdentites, ouvertAuxCommentaires),
    [enVueRole, mesIdentites, ouvertAuxCommentaires],
  )
  // ─── Mentions « @ » (02/11) ───
  // Proposées dès « @ » : les personnes de la fiche — déclarant, coordinateurs,
  // auteurs de l'activité (les plus récents d'abord) —, moi exclu.
  const proposesMention = useMemo<PersonneMentionnee[]>(() => {
    if (!issue) return []
    const moi = new Set(mesIdentites)
    const vus = new Set<string>()
    const res: PersonneMentionnee[] = []
    const ajouter = (nom: string | undefined, upn: string | undefined): void => {
      const cle = upn?.trim().toLowerCase() ?? ''
      if (!nom?.trim() || !cle.includes('@') || cle.includes('#ext#') || moi.has(cle) || vus.has(cle)) return
      vus.add(cle)
      res.push({ nom: nom.trim(), upn: cle })
    }
    ajouter(issue.declarantNom, issue.declarant)
    for (const c of coordinateursDuProbleme(issue)) ajouter(c.nom, c.upn)
    for (const c of [...commentaires].reverse()) ajouter(c.auteur, c.auteurEmail)
    return res
  }, [issue, commentaires, mesIdentites])

  /** Prévient les personnes mentionnées ; renvoie la fin du message de confirmation. */
  const mentionner = useCallback(
    async (texte: string, geste: GesteMention, options: { avant?: string; statut?: Issue['statut'] } = {}): Promise<string> => {
      if (!issue || isFournisseur || !texte.includes('@')) return ''
      const r = await notifierMentions({
        issue: options.statut ? { ...issue, statut: options.statut } : issue,
        geste,
        texte,
        ...(options.avant !== undefined ? { avant: options.avant } : {}),
        auteur: mesIdentites,
        ...(equipement?.familleEquipement ? { modele: equipement.familleEquipement } : {}),
      })
      return resumeMentions(r.prevenus, r.echec, r.essai)
    },
    [issue, isFournisseur, mesIdentites, equipement],
  )

  const corrigerCommentaire = useCallback(
    async (c: Commentaire, texte: string): Promise<boolean> => {
      if (!c.itemId) return false
      try {
        const maj = await modifierCommentaire(c.itemId, c.contenu, texte, mesIdentites)
        setCommentaires((prev) => prev.map((x) => (x.id === c.id ? maj : x)))
        show('Commentaire corrigé' + (await mentionner(texte, 'correction', { avant: c.contenu })), 'success')
        return true
      } catch (err) {
        show(err instanceof Error ? err.message : 'Le commentaire n’a pas pu être corrigé.', 'error')
        return false
      }
    },
    [mesIdentites, show, mentionner],
  )

  const voirDansActivite = useCallback((commentaireId: string): void => {
    setRangVisionneuse(null)
    document.getElementById(`evenement-${commentaireId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  // ─── Actions ───

  // ─── Événements de timeline : best-effort, mais jamais muets ───
  //
  // L'action métier (qualifier, annuler, clore) est la source de vérité : elle
  // est écrite d'abord, en une requête. L'événement de timeline suit. S'il
  // échoue, l'action a QUAND MÊME abouti — il ne faut donc ni afficher
  // « Erreur » (le coordinateur réessaierait sur un problème déjà traité), ni
  // se taire (la timeline aurait un trou que personne ne s'expliquerait).
  //
  // Cause la plus probable d'un tel échec : une valeur de choix manquante dans
  // SCE_Commentaires (field_4 TypeAction, field_6/field_7 statuts).
  async function tracer(
    evenement: Parameters<typeof addCommentaire>[0],
    libelleAction: string,
  ): Promise<void> {
    // Appelé seulement APRÈS une action métier réussie : le problème a changé.
    aEcrit.current = true
    try {
      await addCommentaire(evenement)
      show(libelleAction, 'success')
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[IssueDetail] événement ${evenement.type} non tracé :`, err)
      show(`${libelleAction}, mais l'événement n'a pas pu être ajouté à l'historique`, 'info')
    }
  }

  // ─── Tout le groupe (Lot 14) ───
  //
  // Après l'action sur CETTE fiche (la source de vérité, écrite d'abord), la
  // même action sur chaque sœur encore éligible, relue avant d'écrire
  // (api/groupes.api). Une sœur en échec est nommée : à refaire depuis sa fiche.
  async function appliquerAuGroupe(
    demande: boolean,
    eligible: (i: Issue) => boolean,
    agir: Parameters<typeof appliquerAuxMembres>[2],
  ): Promise<BilanGroupe> {
    if (!demande || membres.length === 0) return BILAN_VIDE
    const bilan = await appliquerAuxMembres(membres.map((m) => m.id), eligible, agir)
    void relireTous()
    return bilan
  }

  function signalerEchecs(bilan: BilanGroupe): void {
    if (bilan.echecs.length === 0) return
    show(
      `Pas appliqué sur ${bilan.echecs.join(', ')} : à refaire depuis ${bilan.echecs.length > 1 ? 'leurs fiches' : 'sa fiche'}`,
      'error',
    )
  }

  async function ajouterUneMachine(eq: Equipement): Promise<void> {
    if (!issue || !itemId || !user || submitting) return
    if (!confirmerEcriture(`Ajouter l’équipement ${eq.numeroSerie}`)) return
    setSubmitting(true)
    try {
      // Nom du compte RÉEL : c'est lui qui signe, même en bascule de rôle.
      const cree = await ajouterMachine(issue, itemId, eq, { upn: user.upn, nom: nomReel || user.nom })
      await tracer(
        { problemId: id, type: 'Modification', contenu: `Équipement ajouté au même défaut : ${eq.numeroSerie} (${cree.id})` },
        `Équipement ${eq.numeroSerie} ajouté : ${cree.id}`,
      )
      await Promise.all([refresh(true), relireTous()])
    } catch (err) {
      show(err instanceof Error ? err.message : "Équipement non ajouté", 'error')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleQualifier(): Promise<void> {
    if (!issue || !user || !itemId || !qualifFamille || !qualifCriticite || qualifying) return
    if (!confirmerEcriture('Qualifier ce problème')) return
    setQualifying(true)
    try {
      // qualifyIssue porte la règle « Ouvert → En cours » : la fiche n'a pas
      // à la connaître, seulement à la tracer si elle a eu lieu.
      const qualification = { famille: qualifFamille, criticite: qualifCriticite }
      const { statutQuitte } = await qualifyIssue(itemId, qualification, issue.statut)
      const bilan = await appliquerAuGroupe(appliquerGroupe, eligibleQualification, async (sItemId, soeur) => {
        const r = await qualifyIssue(sItemId, qualification, soeur.statut)
        return {
          problemId: soeur.id,
          type: 'Qualification',
          contenu: `Problème qualifié avec ${id} : famille « ${qualification.famille} » · criticité « ${qualification.criticite} »`,
          ...(r.statutQuitte ? { ancienStatut: r.statutQuitte, nouveauStatut: 'En cours' as const } : {}),
        }
      })
      await tracer(
        {
          problemId: id,
          type: 'Qualification',
          contenu: `Problème qualifié : famille « ${qualifFamille} » · criticité « ${qualifCriticite} »`,
          ...(statutQuitte
            ? { ancienStatut: statutQuitte, nouveauStatut: 'En cours' as const }
            : {}),
        },
        (statutQuitte ? 'Problème qualifié et passé en cours' : 'Problème qualifié') + resumerBilan(bilan),
      )
      signalerEchecs(bilan)
      setAppliquerGroupe(true)
      setQualifFamille('')
      setQualifCriticite(null)
      await refresh()
    } catch (err) {
      show(err instanceof Error ? err.message : 'Erreur de qualification', 'error')
    } finally {
      setQualifying(false)
    }
  }

  async function handleAnnuler(): Promise<void> {
    const commentaire = commentaireAnnulation.trim()
    if (!issue || !user || !itemId || !motifAnnulation || !commentaire || qualifying) return
    if (!confirmerEcriture('Annuler ce problème')) return
    setQualifying(true)
    try {
      const annulation = { motif: motifAnnulation, commentaire }
      await annulerIssue(itemId, annulation, issue.statut)
      const bilan = await appliquerAuGroupe(appliquerGroupe, eligibleQualification, async (sItemId, soeur) => {
        await annulerIssue(sItemId, annulation, soeur.statut)
        return {
          problemId: soeur.id,
          type: 'Annulation',
          contenu: `Problème annulé avec ${id} — motif « ${annulation.motif} »\n\n${commentaire}`,
          ancienStatut: soeur.statut,
          nouveauStatut: ANNULE,
        }
      })
      const mentions = await mentionner(commentaire, 'annulation', { statut: ANNULE })
      await tracer(
        {
          problemId: id,
          type: 'Annulation',
          contenu: `Problème annulé — motif « ${motifAnnulation} »\n\n${commentaire}`,
          ancienStatut: issue.statut,
          nouveauStatut: ANNULE,
        },
        'Problème annulé' + resumerBilan(bilan) + mentions,
      )
      signalerEchecs(bilan)
      setAppliquerGroupe(true)
      setModeAnnulation(false)
      setMotifAnnulation(null)
      setCommentaireAnnulation('')
      await refresh()
    } catch (err) {
      show(err instanceof Error ? err.message : "Erreur d'annulation", 'error')
    } finally {
      setQualifying(false)
    }
  }

  async function publierAnnotation(image: Blob, texte: string): Promise<void> {
    if (!aAnnoter || !user) return
    const { photo } = aAnnoter
    // Un PDF annoté reste un PDF (annotations en vectoriel, utils/annotationPdf).
    const estPdf = aAnnoter.affichee.isPdf
    const quoi = estPdf ? 'Le PDF annoté' : 'La photo annotée'
    setEnvoiAnnotation(true)
    try {
      const c = await addCommentaire({ problemId: id, type: 'Commentaire', contenu: texte || (estPdf ? 'PDF annoté' : 'Photo annotée') })
      setCommentaires((prev) => [...prev, c])
      // Toujours rattachée à l'original : « Originale » y ramène, même après
      // plusieurs annotations successives.
      const fichier = new File([image], nommerAnnotation(photo.pj.nom, photo.pj.id, estPdf ? 'pdf' : 'jpg'), {
        type: estPdf ? 'application/pdf' : 'image/jpeg',
      })
      const [r] = await uploadFiles(id, [fichier], undefined, c.id)
      if (!r?.success) throw new Error(r?.erreur ?? `${quoi} n’a pas été envoyé${estPdf ? '' : 'e'}.`)
      await refreshPJ()
      setAAnnoter(null)
      show(`${quoi} est publié${estPdf ? '' : 'e'} dans l’activité`, 'success')
    } catch (err) {
      show(err instanceof Error ? err.message : `${quoi} n’a pas été publié${estPdf ? '' : 'e'}`, 'error')
    } finally {
      setEnvoiAnnotation(false)
    }
  }

  async function postCommentaire(): Promise<void> {
    if (!user) return
    const texte = nouveauCommentaire.trim()
    if (!texte && fichiersCommentaire.length === 0) return
    setSubmitting(true)
    try {
      let bilan = BILAN_VIDE
      // Des photos sans texte ont aussi leur événement : sans lui, elles
      // passeraient pour des photos du problème (utils/rattachementPhotos).
      const nbFichiers = fichiersCommentaire.length
      const c = await addCommentaire({
        problemId: id,
        type: 'Commentaire',
        contenu: texte || (nbFichiers > 1 ? `${nbFichiers} fichiers ajoutés` : 'Fichier ajouté'),
      })
      setCommentaires((prev) => [...prev, c])
      // Les personnes mentionnées : une fois, depuis cette fiche (pas depuis les sœurs).
      const mentions = texte ? mentionner(texte, 'commentaire') : Promise.resolve('')
      if (texte) {
        setNouveauCommentaire('')
        // « Une coche pour commenter tout le groupe » : le texte seul, les
        // pièces jointes restent sur cette fiche.
        bilan = await appliquerAuGroupe(commenterGroupe, eligibleCloture, async (_, soeur) => ({
          problemId: soeur.id,
          type: 'Commentaire',
          contenu: texteRepublie(texte, id),
        }))
        setCommenterGroupe(true)
      }
      if (fichiersCommentaire.length > 0) {
        const results = await envoyerFichiers(id, fichiersCommentaire, undefined, c.id)
        const echecs = results.filter((r) => !r.success)
        if (echecs.length > 0) {
          show(`${echecs.length} fichier(s) non uploadé(s)`, 'error')
        }
        setFichiersCommentaire([])
        setShowPicker(false)
        await refreshPJ()
      }
      show(texte ? `Commentaire ajouté${resumerBilan(bilan)}${await mentions}` : 'Fichier(s) ajouté(s)', 'success')
      signalerEchecs(bilan)
    } catch (err) {
      show(err instanceof Error ? err.message : 'Erreur', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // ⚠️ « Prise en charge » (passage manuel Ouvert → En cours) a été RETIRÉE
  //    au Lot 2, à la demande des coordinateurs : c'est désormais la
  //    qualification qui fait entrer un problème en traitement. Les
  //    événements « Prise en charge du problème » déjà écrits restent lisibles
  //    dans la timeline : ce sont des ChangementStatut ordinaires.

  // Références composées à partir de la saisie de clôture. Un numéro vide =
  // pas de référence (elle est facultative) ; un numéro saisi avec une année
  // tronquée = saisie à compléter, qui bloque la clôture plutôt que
  // d'enregistrer une référence introuvable.
  const refsCloture = useMemo(
    () => composerReferences(badgesCloture, refsSaisies),
    [badgesCloture, refsSaisies],
  )
  const refsTq = useMemo(() => composerReferences(tqBadges, tqSaisies), [tqBadges, tqSaisies])

  async function cloturer(): Promise<void> {
    const commentaire = commentaireCloture.trim()
    if (!issue || !user || !itemId || badgesCloture.length === 0) return
    // Le commentaire n'est exigé qu'avec « Autre » (02/10).
    if (!commentaire && commentaireClotureObligatoire(badgesCloture)) return
    if (refsCloture.incomplete) return
    if (porteeAChoisir(nbActifs, porteeCloture)) return
    if (!confirmerEcriture('Clôturer ce problème')) return
    setSubmitting(true)
    try {
      // cloturerIssue persiste commentaire, badges et références SUR le
      // problème, en une requête. Avant le Lot 2, le commentaire était saisi
      // puis jeté par updateIssue sans la moindre erreur.
      const cloture = { commentaire, badges: badgesCloture, references: refsCloture.refs }
      const clos = await cloturerIssue(itemId, cloture, issue.statut)
      const surLeGroupe = porteeSurLeGroupe(nbActifs, porteeCloture)
      const bilan = await appliquerAuGroupe(surLeGroupe, eligibleCloture, async (sItemId, soeur) => {
        const close = await cloturerIssue(sItemId, cloture, soeur.statut)
        return {
          problemId: soeur.id,
          type: 'Cloture',
          contenu:
            `Clôturé avec ${id} — traitement qualité : ${cloture.badges.map((b) => libelleBadge(close, b)).join(' · ')}` +
            (commentaire ? `\n\n${commentaire}` : ''),
          ancienStatut: soeur.statut,
          nouveauStatut: 'Clos',
        }
      })
      // ⚠️ Événement tracé APRÈS et sans faire échouer la clôture : l'ancien
      //    code l'attendait sans filet, si bien qu'un échec d'écriture de la
      //    timeline affichait « Erreur » sur une clôture réussie — et le
      //    coordinateur qui réessayait tombait sur un problème déjà clos.
      const mentions = await mentionner(commentaire, 'cloture', { statut: 'Clos' })
      await tracer(
        {
          problemId: id,
          type: 'Cloture',
          // Les badges — référence comprise — figurent aussi dans
          // l'événement : la timeline doit se suffire à elle-même.
          contenu:
            `Traitement qualité : ${badgesCloture.map((b) => libelleBadge(clos, b)).join(' · ')}` +
            (commentaire ? `\n\n${commentaire}` : ''),
          ancienStatut: issue.statut,
          nouveauStatut: 'Clos',
        },
        'Problème clôturé' + resumerBilan(bilan) + mentions,
      )
      signalerEchecs(bilan)
      setPorteeCloture(null)
      setCommentaireCloture('')
      setBadgesCloture([])
      setRefsSaisies(saisiesDepuis())
      await refresh()
    } catch (err) {
      show(err instanceof Error ? err.message : 'Erreur de clôture', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Référence renseignée après la clôture (Lot 3 bis) ───
  //
  // Le numéro de FNC-T ou de NC-S n'existe pas toujours au moment de clore :
  // il se renseigne après coup, depuis la fiche du problème clos. C'est une
  // modification d'un enregistrement qualité — tracée comme telle.

  function ouvrirEditionRef(badge: BadgeAvecReference): void {
    if (!issue) return
    const actuelle = badge === 'FNC-T' ? issue.refFnct : issue.refNcs
    setSaisieEdition(decomposerReference(actuelle) ?? { annee: ANNEE_COURANTE, numero: '' })
    setRefEnEdition(badge)
  }

  async function enregistrerRef(): Promise<void> {
    if (!issue || !itemId || !refEnEdition) return
    const badge = refEnEdition
    // Numéro vidé = référence retirée ; sinon, elle doit être complète.
    const nouvelle =
      saisieEdition.numero.trim() === ''
        ? ''
        : composerReference(badge, saisieEdition.annee, saisieEdition.numero)
    if (nouvelle === null) {
      show('Référence incomplète : année sur 4 chiffres, numéro de 1 à 3 chiffres.', 'error')
      return
    }
    const avant = (badge === 'FNC-T' ? issue.refFnct : issue.refNcs) ?? ''
    if (avant === nouvelle) {
      setRefEnEdition(null)
      return
    }
    if (!confirmerEcriture(`Enregistrer la référence ${badge}`)) return
    setSubmitting(true)
    try {
      await renseignerReferences(
        itemId,
        badge === 'FNC-T' ? { fnct: nouvelle } : { ncs: nouvelle },
        issue,
      )
      await tracer(
        {
          problemId: id,
          type: 'Modification',
          contenu:
            nouvelle === ''
              ? `Référence ${badge} retirée (était ${avant})`
              : avant === ''
                ? `Référence ${badge} renseignée : ${nouvelle}`
                : `Référence ${badge} corrigée : ${avant} → ${nouvelle}`,
        },
        'Référence enregistrée',
      )
      setRefEnEdition(null)
      await refresh()
    } catch (err) {
      show(err instanceof Error ? err.message : "Erreur d'enregistrement", 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Traitement qualité préparé avant la clôture (30/09) ───
  //
  // Deux gestes séparés : la carte « Traitement qualité » s'enregistre sans
  // clore (on sait déjà qu'une NC-S sera ouverte) ; la clôture repart de ce
  // qui y est enregistré et n'exige plus que son commentaire.
  function ouvrirTq(): void {
    if (!issue) return
    setTqBadges(issue.badgesCloture ?? [])
    setTqSaisies(saisiesDepuis(issue))
    setTqNote(issue.commentaireCloture ?? '')
    setTqOuvert(true)
  }

  function ouvrirCloture(): void {
    if (!issue) return
    setBadgesCloture(issue.badgesCloture ?? [])
    setRefsSaisies(saisiesDepuis(issue))
    setCommentaireCloture(issue.commentaireCloture ?? '')
    setClotureChangeTraitement(!(issue.badgesCloture?.length))
    setPorteeCloture(null)
    setClotureOuverte(true)
  }

  async function preparer(): Promise<void> {
    if (!issue || !itemId || submitting || refsTq.incomplete) return
    const commentaire = tqNote.trim()
    const badges = ordonnerBadges(tqBadges)
    const inchange =
      badges.join('|') === ordonnerBadges(issue.badgesCloture ?? []).join('|') &&
      (refsTq.refs.fnct ?? '') === (badges.includes('FNC-T') ? (issue.refFnct ?? '') : '') &&
      (refsTq.refs.ncs ?? '') === (badges.includes('NC-S') ? (issue.refNcs ?? '') : '') &&
      commentaire === (issue.commentaireCloture ?? '').trim()
    if (inchange) {
      setTqOuvert(false)
      return
    }
    if (!confirmerEcriture('Enregistrer le traitement qualité')) return
    setSubmitting(true)
    try {
      const apres = await preparerTraitementQualite(
        itemId,
        { badges, references: refsTq.refs, commentaire },
        issue.statut,
      )
      await tracer(
        {
          problemId: id,
          type: 'Modification',
          contenu:
            badges.length === 0 && commentaire === ''
              ? 'Traitement qualité prévu retiré'
              : `Traitement qualité prévu : ${
                  badges.length > 0 ? badges.map((b) => libelleBadge(apres, b)).join(' · ') : 'badge à choisir'
                }${commentaire ? `

${commentaire}` : ''}`,
        },
        'Traitement qualité enregistré',
      )
      setTqOuvert(false)
      await refresh()
    } catch (err) {
      show(err instanceof Error ? err.message : "Erreur d'enregistrement", 'error')
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Édition par le coordinateur (Lot 6) ───
  //
  // Une requête (modifierIssue, qui relit la fiche avant d'écrire), puis UN
  // événement « Modification » listant chaque champ avant → après — en
  // best-effort, comme les autres transitions.
  async function enregistrerEdition(saisie: SaisieModification): Promise<void> {
    if (!issue || !itemId || submitting) return
    if (!confirmerEcriture('Modifier ce problème')) return
    setSubmitting(true)
    try {
      const { changements, suiviPar } = await modifierIssue(itemId, issue, saisie, {
        admin: estAdminAffiche,
      })
      // Décision 5 : le réaiguillage se dit, il ne se cache pas — le
      // problème vient peut-être de sortir du périmètre de qui l'a corrigé.
      const message =
        (suiviPar ? `Problème modifié — désormais suivi par ${suiviPar.join(' et ')}` : 'Problème modifié') +
        (await mentionner(saisie.description, 'description', { avant: issue.description }))
      // Titre, description, phase (23/10) : des corrections, sans événement —
      // leur historique est sur le champ. Le reste s'annonce dans l'activité.
      const annonces = changementsAnnonces(changements)
      if (annonces.length > 0) {
        await tracer({ problemId: id, type: 'Modification', contenu: resumerModification(annonces, suiviPar ?? undefined) }, message)
      } else {
        aEcrit.current = true
        show(message, 'success')
      }
      setEdition(false)
      await refresh()
    } catch (err) {
      // Sur un refus, on referme l'édition et on relit la fiche : le cas le
      // plus probable est « modifié entre-temps », et la fiche doit alors
      // montrer l'état réel plutôt que la saisie devenue caduque.
      show(err instanceof Error ? err.message : "Erreur d'enregistrement", 'error')
      setEdition(false)
      void refresh(true)
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Réouverture (Lot 7, administrateur) ───
  //
  // Clos ou annulé → En cours (ou Ouvert s'il reste à qualifier). Les données
  // de fin sont effacées de la fiche par rouvrirIssue ; l'événement les cite
  // en entier, pour que rien ne se perde.
  async function rouvrir(): Promise<void> {
    const commentaire = (reouverture ?? '').trim()
    if (!issue || !itemId || !commentaire || submitting) return
    if (!confirmerEcriture('Rouvrir ce problème')) return
    setSubmitting(true)
    try {
      const { nouveauStatut } = await rouvrirIssue(itemId, issue, commentaire)
      await tracer(
        {
          problemId: id,
          type: 'ChangementStatut',
          contenu: resumerReouverture(issue, commentaire),
          ancienStatut: issue.statut,
          nouveauStatut,
        },
        `Problème rouvert — ${nouveauStatut.toLowerCase()}`,
      )
      setReouverture(null)
      await refresh()
    } catch (err) {
      show(err instanceof Error ? err.message : 'Erreur de réouverture', 'error')
      setReouverture(null)
      void refresh(true)
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Render ───

  // En surimpression, la fiche se passe d'AppShell (sinon BottomNav en double
  // et hauteur plein écran). Même mécanique que EquipementDetailPage.
  const dansModale = useDansModale()
  const margeBoutonsModale = useMargeBoutonsModale()
  const estBureau = useEstBureau()
  const ouvrirFiche = useOuvrirFiche()
  // Volet NC-S ouvert : la fiche se réduit à une colonne (cartes sous l'activité).
  const { voletOuvert } = useVoletModale()
  const deuxColonnes = estBureau && !voletOuvert
  const location = useLocation()
  const maintenant = useMaintenant()
  const { presents } = usePresence()

  // ─── Pastilles « Modifié » (02/10) ───
  //
  // Une ligne de l'activité récente cliquée emporte l'ID de son événement ;
  // les champs qu'il a touchés portent une pastille, avec l'ancienne valeur
  // au survol. Rien n'est enregistré.
  // Arrivée par « + › Déclarer une NC-S » (06/10) : le formulaire NC-S s'ouvre
  // une fois la fiche chargée, puis la consigne est retirée de l'historique
  // (un retour arrière ne le rouvre pas).
  const navigate = useNavigate()
  const ouvrirNcsALArrivee = (location.state as EtatModale | null)?.ouvrirNcs === true
  useEffect(() => {
    if (!ouvrirNcsALArrivee || !issue || issue.id !== id) return
    if (!isFournisseur && issue.statut !== 'Annulé') setNcsOuverte(true)
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...(location.state as EtatModale), ouvrirNcs: undefined },
    })
  }, [ouvrirNcsALArrivee, issue, id, isFournisseur, navigate, location.pathname, location.search, location.state])

  const idEvenement = (location.state as EtatModale | null)?.evenement
  const evenementCible = useMemo(
    () => (idEvenement ? commentaires.find((c) => c.id === idEvenement) : undefined),
    [idEvenement, commentaires],
  )
  const champsTouches = useMemo(
    () => (evenementCible ? champsModifies(evenementCible) : null),
    [evenementCible],
  )
  const pastille = (cible: CibleModification, versLaGauche?: boolean): JSX.Element | null => {
    if (!evenementCible || !champsTouches?.has(cible)) return null
    return (
      <PastilleModifie
        date={evenementCible.date}
        avant={champsTouches.get(cible)}
        maintenant={maintenant}
        versLaGauche={versLaGauche}
      />
    )
  }
  // Qui d'autre a cette fiche ouverte : évite de qualifier ou de clore à deux.
  const aussiIci = presents.filter((p) => p.page === location.pathname)
  const Cadre = dansModale ? CadreModale : CadrePage
  const titre = issue?.id ?? id ?? 'Problème'

  if (loading) {
    return (
      <Cadre titre={titre} onBack={retour}>
        <div className="p-4 animate-pulse space-y-3">
          {[60, 120, 120].map((h, i) => (
            <div
              key={i}
              style={{ height: h, backgroundColor: 'var(--color-gray-10)', borderRadius: 8 }}
            />
          ))}
        </div>
      </Cadre>
    )
  }

  if (error || !issue) {
    return (
      <Cadre titre={titre} onBack={retour}>
        <div
          className="m-4 p-4 rounded text-center"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-error)',
          }}
        >
          {error ?? 'Problème introuvable'}
        </div>
      </Cadre>
    )
  }

  // `termine` et non `clos` : un problème annulé est lui aussi sorti du
  // circuit — plus de qualification, plus de clôture.
  const termine = estTermine(issue)
  const annule = issue.statut === ANNULE
  // Commentaires ouverts sur un clos (Guillaume, 26/10) : on y revient après
  // coup (« revu au test », « même défaut ailleurs »). Un annulé reste fermé.
  const peutCommenter = commentairesOuverts(issue.statut)
  const actionsOuvertes = peutPiloter && !termine

  // Lot 6 — décision 6 : coordinateur du métier ACTUEL, ou administrateur
  // (c'est `peutPiloter`). Un coordinateur ne peut donc pas s'attribuer un
  // problème d'un autre métier : il faut en être déjà le coordinateur.
  // Actif ou clos ; un annulé ne se modifie plus — sauf par l'administrateur
  // (Lot 7, édition totale).
  const peutEditer = peutPiloter && (!annule || estAdminAffiche)

  // ─── Rouvrir (Lot 7) ───
  // Dans la carte de fin (clos ou annulé), administrateur seulement. Replié
  // derrière un bouton discret : c'est l'exception, et un commentaire est
  // exigé — on doit pouvoir lire POURQUOI un problème terminé a été rouvert.
  const statutApresReouverture = statutReouverture(issue)
  const blocReouverture = estAdminAffiche && termine && (
    reouverture === null ? (
      <Button
        variant="ghost"
        onClick={() => setReouverture('')}
        disabled={submitting || edition}
        iconLeft={<RotateCcw size={16} />}
      >
        Rouvrir…
      </Button>
    ) : (
      <div style={{ marginTop: 8 }}>
        <Etiquette htmlFor="commentaire-reouverture">Pourquoi rouvrir ? *</Etiquette>
        <textarea
          id="commentaire-reouverture"
          value={reouverture}
          onChange={(e) => setReouverture(e.target.value)}
          rows={3}
          aria-required="true"
          disabled={submitting}
          className="w-full p-3 rounded-lg resize-none outline-none"
          style={{
            fontSize: 'var(--font-size-copy2)',
            backgroundColor: 'var(--color-white)',
            border: '1px solid var(--color-border)',
          }}
        />
        <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', margin: '6px 0 8px' }}>
          Le problème repassera « {statutApresReouverture} ». {annule ? 'Motif et commentaire d\'annulation' : 'Badges, références et commentaire de clôture'} seront
          effacés de la fiche et cités en entier dans l&apos;activité.
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setReouverture(null)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => void rouvrir()}
            disabled={submitting || reouverture.trim() === ''}
            iconLeft={<RotateCcw size={16} />}
          >
            Rouvrir le problème
          </Button>
        </div>
      </div>
    )
  )

  // ─── Déclaration NC-S déposée (05/10) ───
  // Le fichier est dans le dossier de la qualité (acquis) ; le problème prend
  // le badge NC-S, puis l'activité garde le lien vers le fichier.
  async function apresDeclarationNcs(r: ResultatDeclarationNcs, priorite: PrioriteNcs | null): Promise<void> {
    if (!itemId) return
    try {
      await ajouterBadgeNcs(itemId)
    } catch (err) {
      console.error('[IssueDetail] badge NC-S :', err)
      show("Déclaration déposée, mais le badge NC-S n'a pas pu être ajouté au problème", 'error')
    }
    await tracer({ problemId: id, type: 'Modification', contenu: texteEvenementNcs(r.fichier, r.lien, priorite) }, 'NC-S envoyée à la qualité')
    await refresh(true)
  }

  // ─── FNC-T ouverte (lot F2) ───
  // La FNC-T est écrite (SCE_FNCT). Hors essai, le problème et les sœurs
  // retenues prennent sa référence et le badge FNC-T, puis une ligne
  // d'activité. La fenêtre montre ensuite « C'est ouvert » (« Voir la fiche FNC-T »).
  async function apresCreationFnct(numero: string, machines: readonly string[]): Promise<void> {
    if (!itemId) return
    // En essai, rien n'est écrit sur le problème (l'écran « C'est ouvert » le dit).
    if (!FNCT_EN_ESSAI) {
      try {
        await lierFnct(itemId, numero)
        await tracer({ problemId: id, type: 'Modification', contenu: `FNC-T ouverte : ${numero}` }, `${numero} ouverte`)
      } catch (err) {
        console.error('[IssueDetail] lien FNC-T :', err)
        show(`${numero} créée, mais le problème n'a pas pu la référencer`, 'error')
      }
      const cles = new Set(machines.map((m) => m.toUpperCase()))
      const soeurs = membres.filter((m) => cles.has(m.numeroSerie.toUpperCase()))
      if (soeurs.length > 0) {
        const bilan = await appliquerAuxMembres(
          soeurs.map((m) => m.id),
          (i) => i.statut !== 'Annulé',
          async (sItemId, i) => {
            await lierFnct(sItemId, numero)
            return { problemId: i.id, type: 'Modification', contenu: `FNC-T ouverte : ${numero}` }
          },
        )
        if (bilan.echecs.length > 0) show(`${numero} : à référencer à la main sur ${bilan.echecs.join(', ')}`, 'error')
      }
      await refresh(true)
    }
  }

  // Les déclarations déposées, relues dans l'activité, de la plus ancienne à
  // la plus récente. (Pas de hook : on est après les retours anticipés.)
  const declarationsNcs = declarationsNcsDe(commentaires)
  const declarationNcs = declarationsNcs.at(-1) ?? null

  // ─── Traitement qualité (30/09) ───
  // Dans la carte Suivi, sous le coordinateur qui le mène : la clôture tant
  // que le problème est actif, puis son résultat — badges, références FNC-T /
  // NC-S (modifiables après coup) et commentaire de fin. Remplace la carte
  // verte « Clos » du haut de la fiche et le formulaire du bas de page.
  const styleBloc: CSSProperties = {
    padding: '10px 0 12px',
    borderTop: '1px solid var(--color-gray-10)',
    fontSize: 14,
  }
  const titreBloc = (icone?: ReactNode, droite?: ReactNode) => (
    <div className="flex items-center justify-between gap-2" style={{ minHeight: 24 }}>
      <span className="flex items-center gap-2" style={{ color: 'var(--color-gray-70)' }}>
        {icone}
        Traitement qualité
      </span>
      {droite}
    </div>
  )

  const resultatCloture = issue.statut === 'Clos' && (
    <div className="flex flex-col gap-2" style={styleBloc}>
      {titreBloc(<CheckCircle2 size={16} color="var(--color-traffic-green)" />)}
      {(issue.badgesCloture?.length ?? 0) > 0 && (
        <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
          {issue.badgesCloture?.map((b) => libelleBadge(issue, b)).join(' · ')}
        </span>
      )}
      {issue.dateCloture && (
        <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>
          Clos le {formatJour(issue.dateCloture)}
        </span>
      )}

      {/* Les références se renseignent ou se corrigent APRÈS la clôture —
          le numéro n'existe pas toujours au moment de clore. */}
      {BADGES_AVEC_REFERENCE.filter((b) => issue.badgesCloture?.includes(b)).map((b) => {
        const ref = b === 'FNC-T' ? issue.refFnct : issue.refNcs
        return refEnEdition === b ? (
          <div key={b} className="flex flex-col gap-2">
            <Etiquette htmlFor={`ref-apres-${b}`}>N° de {b}</Etiquette>
            <ChampReference
              id={`ref-apres-${b}`}
              badge={b}
              valeur={saisieEdition}
              onChange={setSaisieEdition}
              desactive={submitting}
            />
            <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', margin: 0 }}>
              Videz le numéro pour retirer la référence.
            </p>
            <div className="flex gap-2">
              <Button variant="primary" onClick={() => void enregistrerRef()} disabled={submitting}>
                Enregistrer
              </Button>
              <Button variant="ghost" onClick={() => setRefEnEdition(null)} disabled={submitting}>
                Annuler
              </Button>
            </div>
          </div>
        ) : (
          <div key={b} className="flex items-center justify-between gap-2" style={{ minHeight: 44 }}>
            <div className="flex flex-col" style={{ minWidth: 0 }}>
              <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>N° de {b}</span>
              <span
                style={{
                  fontWeight: ref ? 500 : 400,
                  fontStyle: ref ? 'normal' : 'italic',
                  color: ref ? 'var(--color-text-primary)' : 'var(--color-gray-70)',
                  overflowWrap: 'anywhere',
                }}
              >
                {ref ?? 'Non renseigné'}
              </span>
            </div>
            {/* Même droit que la clôture : coordinateur du métier ou admin. */}
            {peutPiloter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => ouvrirEditionRef(b)}
                disabled={submitting || refEnEdition !== null}
              >
                {ref ? 'Modifier' : 'Ajouter'}
              </Button>
            )}
          </div>
        )
      })}

      {issue.commentaireCloture && (
        <p
          style={{
            margin: 0,
            padding: '10px 12px',
            borderRadius: 8,
            backgroundColor: 'var(--color-gray-5)',
            lineHeight: '20px',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {issue.commentaireCloture}
        </p>
      )}
      {blocReouverture}
    </div>
  )

  // Traitement préparé sur un problème encore actif (30/09) : on sait déjà
  // qu'une NC-S sera ouverte, le problème n'est pas encore clôturable. La
  // carte ne fait QUE ça — enregistrer, jamais clore : la clôture est une
  // section à part, en bas de la fiche.
  const traitementPrevu =
    estActif(issue) &&
    ((issue.badgesCloture?.length ?? 0) > 0 || (issue.commentaireCloture ?? '').trim() !== '')
  const badgesPrevus = (issue.badgesCloture?.length ?? 0) > 0 && (
    <div className="flex flex-wrap gap-2">
      {issue.badgesCloture?.map((b) => (
        <Badge key={b} badgeCloture={b} libelle={libelleBadge(issue, b)} />
      ))}
    </div>
  )
  const styleNote: CSSProperties = {
    margin: 0,
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: 'var(--color-gray-5)',
    lineHeight: '20px',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  }

  const carteTraitement = !estActif(issue)
    ? null
    : tqOuvert && actionsOuvertes
      ? (
        <div className="flex flex-col gap-3" style={styleBloc}>
          {titreBloc()}
          <ChoixTraitementQualite
            idPrefixe="tq"
            badges={tqBadges}
            onBadges={setTqBadges}
            saisies={tqSaisies}
            onSaisies={setTqSaisies}
            incomplete={refsTq.incomplete}
            desactive={submitting}
          />
          <div>
            <Etiquette htmlFor="tq-note">Note — facultative</Etiquette>
            <textarea
              id="tq-note"
              value={tqNote}
              onChange={(e) => setTqNote(e.target.value)}
              placeholder="Ex. : NC-S à ouvrir chez le fournisseur"
              rows={3}
              disabled={submitting}
              className="w-full p-3 rounded-lg resize-none outline-none"
              style={{
                fontSize: 'var(--font-size-copy2)',
                backgroundColor: 'var(--color-white)',
                border: '1px solid var(--color-border)',
              }}
            />
          </div>
          {refsTq.incomplete && (
            <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', margin: 0 }}>
              Référence {refsTq.incomplete} incomplète : année sur 4 chiffres.
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setTqOuvert(false)} disabled={submitting}>
              Annuler
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={() => void preparer()}
              disabled={submitting || refsTq.incomplete !== null}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      )
      : traitementPrevu
        ? (
          <div className="flex flex-col gap-2" style={styleBloc}>
            {titreBloc(
              undefined,
              actionsOuvertes && (
                <button
                  type="button"
                  onClick={ouvrirTq}
                  disabled={submitting}
                  aria-label="Modifier le traitement qualité"
                  title="Modifier le traitement qualité"
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: 36, height: 36, margin: '-6px -8px -6px 0', borderRadius: 18, border: 'none', background: 'transparent', color: 'var(--color-black)' }}
                >
                  <PenLine size={16} />
                </button>
              ),
            )}
            {badgesPrevus}
            {issue.commentaireCloture?.trim() && <p style={styleNote}>{issue.commentaireCloture}</p>}
          </div>
        )
        : actionsOuvertes && (
          <button
            type="button"
            onClick={ouvrirTq}
            disabled={submitting}
            className="flex items-center gap-2 w-full"
            style={{
              minHeight: 44,
              padding: 0,
              border: 'none',
              borderTop: '1px solid var(--color-gray-10)',
              background: 'transparent',
              color: 'var(--color-black)',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <Plus size={16} />
            Définir le traitement qualité
          </button>
        )

  const traitementQualite = resultatCloture || carteTraitement || null

  // ─── Déclaration NC-S (05/10) ───
  // Le formulaire de réclamation fournisseur de la qualité, pré-rempli et
  // déposé dans son dossier (components/issues/DeclarationNcs). Ouvert à tous
  // les internes le 06/10 — comme le fichier Excel qu'on remplissait avant.
  // Pas aux fournisseurs : c'est une réclamation CONTRE un fournisseur.
  // ─── Badge NC-S de l'en-tête (06/10, piste B) ───
  // Il dit où en est la NC-S chez la qualité ; un clic montre le détail et,
  // tant que le N° manque, sa recherche dans le registre. Plus de ligne NC-S
  // dans la carte Suivi : rien d'autre sur la fiche.
  const badgeNcs = !isFournisseur && (
    <BadgeNcs
      refNcs={issue.refNcs}
      declaration={declarationNcs}
      recherche={referenceNcs.etat}
      onChercher={referenceNcs.chercher}
      onChoisir={(c) => {
        if (confirmerEcriture(`Enregistrer le N° ${c.ligne.numero}`)) void referenceNcs.choisir(c)
      }}
    />
  )

  // Une NC-S commencée puis réduite (brouillon du navigateur, 06/10) : on la
  // reprend d'ici, ou par la pastille « NC-S en cours » (globale, BrouillonsNcs).
  const brouillonNcs = !isFournisseur && !annule && !ncsOuverte ? lireBrouillonNcs(issue.id) : null
  const lienNcs = !isFournisseur && !annule && (
    <LigneActionCarte
      icone={<FileSpreadsheet size={18} />}
      titre={brouillonNcs ? 'Reprendre la NC-S en cours' : declarationNcs ? 'Déclarer une autre NC-S' : 'Déclarer une NC-S'}
      detail="Défaut fournisseur · à la qualité"
      onClick={() => setNcsOuverte(true)}
    />
  )

  // ─── FNC-T (lot F2, piste B du 06/10) ───
  // Une ligne d'action au-dessus de la NC-S : « Ouvrir une FNC-T », ou la FNC-T
  // ouverte avec l'avancement de ses actions (une seule ligne : le relevé P&ID / PPL
  // part de « Ouvrir une FNC-T », 14/10). Administrateur et testeurs pendant la construction. `versionFnct` remonte la ligne
  // après un écart relevé : elle relit les listes (cache vidé par l'écriture).
  const ligneFnct = !isFournisseur && accesFnct(user?.upn) && !FNCT_MASQUEE && !annule && (
    <Suspense fallback={null}>
      <LigneFnctProbleme
        key={versionFnct}
        refFnct={issue.refFnct}
        issueId={issue.id}
        upn={enVueRole || !user ? '' : user.upn.toLowerCase()}
        machines={[issue.numeroSerie]}
        onOuvrir={() => setFenetreFnct('escalade')}
        onFiche={(numero: string) => ouvrirFiche(lienFnct(numero))}
      />
    </Suspense>
  )


  // ─── Clôture (section à part, en bas de fiche — 30/09) ───
  // Coordinateur du métier ou administrateur. Badge obligatoire (demande 5) ;
  // commentaire (demande 4) obligatoire seulement avec « Autre » (02/10). Le
  // traitement qualité déjà enregistré est repris en résumé ; ses bulles ne
  // s'affichent que s'il manque, ou sur « Modifier ».
  const commentaireRequis = commentaireClotureObligatoire(badgesCloture)
  const sectionCloture = actionsOuvertes && (
    !clotureOuverte ? (
      <div className="px-4 pb-6">
        <Button variant="secondary" fullWidth onClick={ouvrirCloture} iconLeft={<CheckCircle2 size={18} />}>
          Clôturer le problème…
        </Button>
      </div>
    ) : (
      <section className="px-4 pb-6">
        <div
          className="flex flex-col gap-4 p-4 rounded-lg"
          style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-white)' }}
        >
          <div className="flex items-center justify-between">
            <h3 style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 600, margin: 0 }}>
              Clôturer le problème
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setClotureOuverte(false)}>
              Replier
            </Button>
          </div>

          <div>
            <Etiquette>Traitement qualité *</Etiquette>
            {clotureChangeTraitement ? (
              <ChoixTraitementQualite
                idPrefixe="cloture"
                badges={badgesCloture}
                onBadges={setBadgesCloture}
                saisies={refsSaisies}
                onSaisies={setRefsSaisies}
                incomplete={refsCloture.incomplete}
                desactive={submitting}
              />
            ) : (
              <div className="flex items-center justify-between gap-2">
                {badgesPrevus}
                <Button variant="ghost" size="sm" onClick={() => setClotureChangeTraitement(true)} disabled={submitting}>
                  Modifier
                </Button>
              </div>
            )}
          </div>

          <div>
            <Etiquette htmlFor="commentaire-cloture">
              {commentaireRequis ? 'Commentaire de clôture *' : 'Commentaire de clôture — facultatif'}
            </Etiquette>
            <ZoneMentions
              id="commentaire-cloture"
              value={commentaireCloture}
              onValeur={setCommentaireCloture}
              proposes={proposesMention}
              placeholder={commentaireRequis ? 'Comment le problème a-t-il été traité ?' : 'Un mot de contexte, si utile'}
              rows={3}
              aria-required={commentaireRequis}
              className="w-full p-3 rounded-lg resize-none outline-none"
              style={{
                fontSize: 'var(--font-size-copy2)',
                backgroundColor: 'var(--color-white)',
                border: '1px solid var(--color-border)',
              }}
            />
          </div>

          {/* Un défaut sur plusieurs équipements (02/11) : on demande, sans
              réponse par défaut — une case cochée d'office a clos un groupe
              entier sans qu'on le voie. */}
          {nbActifs > 0 && (
            <div>
              <Etiquette>
                {`Même défaut sur ${nbActifs > 1 ? `${libelleMachines(nbActifs)} encore ouverts` : 'un autre équipement encore ouvert'} : que clore ? *`}
              </Etiquette>
              <GroupeBulles libelle="Que clore ?">
                <Bulle
                  role="radio"
                  libelle={`Seulement ${issue.numeroSerie || issue.id}`}
                  selectionne={porteeCloture === 'seul'}
                  onClick={() => setPorteeCloture('seul')}
                  desactive={submitting}
                />
                <Bulle
                  role="radio"
                  libelle={`Tout le groupe : ${libelleMachines(nbActifs + 1)}`}
                  selectionne={porteeCloture === 'groupe'}
                  onClick={() => setPorteeCloture('groupe')}
                  desactive={submitting}
                />
              </GroupeBulles>
            </div>
          )}

          {/* Le bouton désactivé seul ne dit pas POURQUOI il l'est : sur un
              écran d'atelier, un bouton gris sans explication passe pour une
              panne. Dit ce qui manque, pas seulement que quelque chose manque. */}
          <div className="flex flex-col gap-2">
            <p style={{ fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)', margin: 0 }}>
              {badgesCloture.length === 0
                ? 'Choisissez au moins un traitement qualité.'
                : refsCloture.incomplete
                  ? `Référence ${refsCloture.incomplete} incomplète : année sur 4 chiffres.`
                  : commentaireRequis && commentaireCloture.trim() === ''
                    ? 'Avec « Autre », le commentaire est obligatoire.'
                    : porteeAChoisir(nbActifs, porteeCloture)
                      ? `Choisissez : ${issue.numeroSerie || 'ce problème'} seul ou tout le groupe.`
                      : 'Prêt à clore.'}
            </p>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => void cloturer()}
              disabled={
                submitting ||
                (commentaireRequis && commentaireCloture.trim() === '') ||
                badgesCloture.length === 0 ||
                refsCloture.incomplete !== null ||
                porteeAChoisir(nbActifs, porteeCloture)
              }
              iconLeft={<CheckCircle2 size={18} />}
            >
              {porteeSurLeGroupe(nbActifs, porteeCloture) ? `Clore les ${nbActifs + 1} problèmes` : 'Marquer comme clos'}
            </Button>
          </div>
        </div>
      </section>
    )
  )

  // ─── Cartes Équipement et Suivi ───
  // Colonne de droite sur bureau, sous la description sur téléphone.
  // OF, désignation et catégorie ne sont plus ici : la fiche équipement les a.
  const cartes = (
    <>
      <section style={STYLE_CARTE}>
        <h3 style={STYLE_TITRE_CARTE}>Équipement {pastille('equipement')}</h3>
        {issue.numeroSerie ? (
          <button
            type="button"
            onClick={() => ouvrirFiche(lienEquipement(issue.numeroSerie))}
            aria-label={`Ouvrir la fiche de l'équipement ${issue.numeroSerie}`}
            className="flex items-center gap-3 w-full text-left"
            style={{
              minHeight: 56,
              padding: 0,
              border: 'none',
              borderBottom: issue.pn ? '1px solid var(--color-gray-10)' : 'none',
              background: 'transparent',
              color: 'var(--color-black)',
            }}
          >
            <Package size={22} color="var(--color-gray-70)" />
            <span className="flex flex-col flex-1" style={{ minWidth: 0 }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{issue.numeroSerie}</span>
              {/* Trois états : modèle connu, absent du référentiel, pas encore résolu. */}
              {equipement?.familleEquipement && (
                <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>{equipement.familleEquipement}</span>
              )}
              {equipement === null && (
                <span style={{ fontSize: 13, color: 'var(--color-gray-70)', fontStyle: 'italic' }}>
                  Absent du référentiel
                </span>
              )}
            </span>
            <ChevronRight size={18} color="var(--color-gray-60)" />
          </button>
        ) : (
          <p style={{ margin: '4px 0 12px', fontSize: 14, color: 'var(--color-gray-70)' }}>Aucun équipement</p>
        )}
        {issue.pn && (
          <button
            type="button"
            onClick={() => ouvrirFiche(lienProjet(issue.pn))}
            aria-label={`Ouvrir le projet ${issue.pn}`}
            className="flex items-center gap-3 w-full text-left"
            style={{ minHeight: 48, padding: 0, border: 'none', background: 'transparent', color: 'var(--color-black)' }}
          >
            <FolderOpen size={22} color="var(--color-gray-70)" />
            <span className="flex-1" style={{ fontSize: 15 }}>
              Projet <strong style={{ fontWeight: 600 }}>{issue.pn}</strong>
            </span>
            <ChevronRight size={18} color="var(--color-gray-60)" />
          </button>
        )}
        {/* Chef de projet (30/09, venu de la carte Suivi) : il porte le projet,
            il se lit sous lui. Hors périmètre d'un guest B2B. */}
        {!isFournisseur && equipement?.chefProjet && (
          <button
            type="button"
            onClick={() => ouvrirFiche(lienChefProjet(equipement.chefProjet ?? ''))}
            aria-label={`Voir les projets de ${equipement.chefProjet}`}
            className="flex items-center gap-3 w-full text-left"
            style={{
              minHeight: 48,
              padding: 0,
              border: 'none',
              borderTop: '1px solid var(--color-gray-10)',
              background: 'transparent',
              color: 'var(--color-black)',
            }}
          >
            <UserRound size={22} color="var(--color-gray-70)" />
            <span className="flex flex-col flex-1" style={{ minWidth: 0 }}>
              <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>Chef de projet</span>
              <span style={{ fontSize: 15, fontWeight: 600, overflowWrap: 'anywhere' }}>{equipement.chefProjet}</span>
            </span>
            <ChevronRight size={18} color="var(--color-gray-60)" />
          </button>
        )}
        {/* Fiche seule (30/09) : la question « même défaut ailleurs ? » tient
            en une ligne discrète ici, plutôt qu'une carte entière. */}
        {membres.length === 0 && peutPiloter && !termine && itemId !== null && (
          <LienAutreMachine issue={issue} onAjouter={ajouterUneMachine} enCours={submitting} />
        )}
      </section>

      {/* Même défaut sur d'autres machines (Lot 14) : les fiches sœurs, et
          « Ajouter une machine » pour qui pilote un problème actif. */}
      <CarteGroupe
        issue={issue}
        membres={membres}
        peutAjouter={peutPiloter && !termine && itemId !== null}
        onAjouter={ajouterUneMachine}
        enCours={submitting}
      />

      <section style={STYLE_CARTE}>
        <h3 style={STYLE_TITRE_CARTE}>Suivi</h3>
        <LigneSuivi label="Famille" discrete={familleAQualifier} action={pastille('famille', true)}>{issue.famille}</LigneSuivi>
        <LigneSuivi label="Phase" action={pastille('phase', true)}>
          {issue.phase}
          {iconeHistorique('phase')}
          {blocHistorique('phase')}
        </LigneSuivi>
        {/* Coordinateur : hors périmètre d'un guest B2B. Le traitement qualité
            suit, sous celui qui le mène. */}
        {!isFournisseur && coordinateurSuivi && (
          <LigneSuivi label="Coordinateur" discrete={coordinateurSuivi.enAttente}>
            {coordinateurSuivi.enAttente
              ? `${coordinateurSuivi.nom} — qualification attendue`
              : coordinateurSuivi.nom}
          </LigneSuivi>
        )}
        {traitementQualite}
        {ligneFnct}
        {lienNcs}
      </section>

      {/* Documentation (Lot 13) : EDE en ELEC / AUTOM, N° de plan en MECA
          (déplacé de la carte Suivi). Tous les internes (15/10). */}
      <SectionDocumentation
        issue={issue}
        itemId={itemId}
        peutEditer={!isFournisseur && !annule}
        tous={tous}
        historique={historique}
        onModifie={async (message) => {
          // Une correction (23/10) : pas d'événement, l'historique est sur la ligne.
          aEcrit.current = true
          show(message, 'success')
          await refresh(true)
        }}
      />
    </>
  )

  const boutonRafraichir = (
    <BoutonRafraichir enCours={rafraichissement} onClick={() => void rafraichir()} />
  )

  // ─── Actions de l'en-tête (20/10) ───
  // Au bureau : Envoyer au BE, la cloche, Modifier, en icônes. Au téléphone,
  // trois icônes ne tiennent pas à côté des badges : Envoyer et Modifier passent
  // dans un menu ⋯ (libellés en clair, il n'y a pas de survol), la cloche reste.
  const envoiPossible = peutPiloter && !annule
  const envoiEnCours = !envoiBEAccessible() && !estAdminAffiche
  const modifierPossible = peutEditer && !edition
  const menuTelephone = !estBureau && envoiPossible && modifierPossible

  return (
    <Cadre titre={titre} onBack={retour} action={dansModale ? undefined : boutonRafraichir}>
      {/* ─── Fiche v2 : en-tête ───
          Statut, criticité (un badge : plus de bandeau pleine largeur), métier
          et badges de clôture sur une ligne ; puis le titre et « déclaré par ».
          En surimpression, l'identifiant vit ici : la fenêtre n'a pas de
          barre de titre. */}
      <section
        className="px-4 pt-4 pb-4 flex flex-col gap-3"
        style={{ borderBottom: '1px solid var(--color-border)' }}
      >
        <div className="flex flex-wrap items-center gap-2" style={{ paddingRight: margeBoutonsModale }}>
          {dansModale && (
            <span style={{ fontSize: 14, color: 'var(--color-gray-70)', marginRight: 4 }}>{issue.id}</span>
          )}
          {/* Même langage que les listes (02/10, ui/EtatProbleme), avec le mot :
              la fiche a la place. Pas de criticité tant que le problème est à
              qualifier (le statut le dit déjà) ni pour un annulé (écarté, pas
              qualifié : l'afficher inviterait à le qualifier). */}
          <span className="fiche-etat">
            <StatutLibelle statut={issue.statut} />
          </span>
          {!annule && issue.statut !== 'Ouvert' && (
            <span className="fiche-etat">
              <CriticiteLibellee criticite={issue.criticite} />
            </span>
          )}
          {!annule && issue.statut !== 'Ouvert' && pastille('criticite')}
          <Badge metier={issue.metier} />
          {issue.sujet && <Badge sujet={issue.sujet} metierDuSujet={issue.metier} />}
          {pastille('metier')}
          {/* Lot 3 bis : la référence qualité, quand elle existe, remplace
              le code — « FNC-T_2026-55 » plutôt que « FNC-T ». */}
          {issue.statut === 'Clos' &&
            issue.badgesCloture?.map((b) =>
              b === 'NC-S' && badgeNcs ? (
                <span key={b}>{badgeNcs}</span>
              ) : (
                <Badge key={b} badgeCloture={b} libelle={libelleBadge(issue, b)} />
              ),
            )}
          {/* NC-S déclarée (05/10) ou N° connu : un fait, pas un traitement prévu —
              elle se voit en tête sans attendre la clôture, avec son état chez la
              qualité (06/10). */}
          {issue.statut !== 'Clos' && (declarationNcs || issue.refNcs) && issue.badgesCloture?.includes('NC-S') &&
            (badgeNcs || <Badge badgeCloture="NC-S" libelle={libelleBadge(issue, 'NC-S')} />)}
          {/* FNC-T ouverte (lot F2) : comme la NC-S, en tête sans attendre la
              clôture ; un clic ouvre sa fiche (administrateur, construction). */}
          {issue.statut !== 'Clos' && issue.refFnct && issue.badgesCloture?.includes('FNC-T') &&
            (estAdminAffiche ? (
              <button
                type="button"
                onClick={() => ouvrirFiche(lienFnct(issue.refFnct ?? ''))}
                title="Ouvrir la fiche FNC-T"
                style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }}
              >
                <Badge badgeCloture="FNC-T" libelle={issue.refFnct} />
              </button>
            ) : (
              <Badge badgeCloture="FNC-T" libelle={issue.refFnct} />
            ))}
          {/* En bout de ligne, en icônes (26/09) : suivre, modifier (Lot 6) et,
              en surimpression, « Rafraîchir » (en page pleine, il est dans la
              barre de titre). 44 px de cible chacun, le libellé en infobulle. */}
          <span className="flex items-center" style={{ marginLeft: 'auto', gap: 2 }}>
          {/* « Envoyer au BE » (20/10) : à côté de la cloche, pour qui pilote le
              problème ; l'infobulle nomme le BE et dit le dernier envoi. Grisée
              « En cours » tant que l'envoi n'est pas ouvert (admin excepté).
              Au téléphone, elle passe dans le menu ⋯ avec « Modifier ». */}
          {envoiPossible && !menuTelephone && (
            <BoutonEnvoiBE
              metier={issue.metier}
              dernier={dernierEnvoi(commentaires)}
              onClick={() => setEnvoiBEOuvert(true)}
              desactive={submitting}
              enCours={envoiEnCours}
            />
          )}
          {/* Cloche « Suivre » (Lot 12) : pour tous les internes. Une écriture
              qui fait recharger les listes à la fermeture (accueil : « Problèmes
              suivis »). */}
          {itemId && (
            <BoutonSuivre
              issue={issue}
              itemId={itemId}
              onEcrit={() => {
                aEcrit.current = true
                void refresh(true)
              }}
            />
          )}
          {menuTelephone && (
            <MenuActionsFiche
              actions={[
                {
                  id: 'envoi-be',
                  icone: <Send size={18} />,
                  libelle: titreEnvoiBE(issue.metier),
                  detail: envoiEnCours ? DETAIL_ENVOI_BE_EN_COURS : (texteDernierEnvoi(dernierEnvoi(commentaires)) ?? DETAIL_ENVOI_BE),
                  enCours: envoiEnCours,
                  onChoisir: () => setEnvoiBEOuvert(true),
                },
                {
                  id: 'modifier',
                  icone: <PenLine size={18} />,
                  libelle: 'Modifier le problème',
                  onChoisir: () => setEdition(true),
                },
              ]}
            />
          )}
          {modifierPossible && !menuTelephone && (
            <button
              type="button"
              onClick={() => setEdition(true)}
              disabled={submitting}
              aria-label="Modifier ce problème"
              title="Modifier ce problème"
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 44,
                height: 44,
                borderRadius: 22,
                border: 'none',
                background: 'transparent',
                color: 'var(--color-black)',
              }}
            >
              <PenLine size={18} />
            </button>
          )}
          {dansModale && boutonRafraichir}
          </span>
        </div>
        <h2
          style={{
            margin: 0,
            fontSize: estBureau ? 25 : 22,
            lineHeight: estBureau ? '32px' : '28px',
            fontWeight: 600,
          }}
        >
          {issue.titre}
          {pastille('titre')}
          {iconeHistorique('titre')}
        </h2>
        {blocHistorique('titre')}
        <div className="flex items-center gap-3">
          <PastillePersonne
            nom={issue.declarantNom || issue.declarant}
            upn={issue.declarant}
            taille={36}
            fond="var(--color-gray-90)"
            taillePolice={14}
          />
          <div className="flex flex-col" style={{ fontSize: 14 }}>
            <span>
              Déclaré par <strong style={{ fontWeight: 600 }}>{issue.declarantNom || issue.declarant || '—'}</strong>
            </span>
            <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>{quandDeclare(issue.dateDeclaration)}</span>
          </div>
        </div>
        {aussiIci.length > 0 && (
          <span
            className="flex items-center gap-2 self-start"
            style={{
              minHeight: 32,
              padding: '4px 12px 4px 4px',
              borderRadius: 16,
              backgroundColor: 'var(--color-gray-5)',
              fontSize: 13,
              color: 'var(--color-gray-90)',
            }}
          >
            <PastillePersonne
              nom={aussiIci[0]?.nom ?? ''}
              upn={aussiIci[0]?.upn}
              taille={24}
              fond="var(--color-gray-90)"
              taillePolice={10}
            />
            {aussiIci[0]?.nom}
            {aussiIci.length > 1 ? ` et ${aussiIci.length - 1} autre${aussiIci.length > 2 ? 's' : ''}` : ''}
            {aussiIci.length > 1 ? ' consultent' : ' consulte'} aussi cette fiche
          </span>
        )}
      </section>

      <div
        style={
          deuxColonnes
            ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 300px', gap: 8, alignItems: 'start', paddingRight: 16 }
            : undefined
        }
      >
        <div className="flex flex-col" style={{ minWidth: 0 }}>
      {/* Problème annulé : le commentaire de fin, lisible sans fouiller la
          timeline — et seul endroit où il apparaît si l'événement de timeline
          n'a pas pu être écrit. */}
      {annule && (
        <section
          className="mx-4 mt-4 p-4 rounded-lg"
          style={{ border: '1px solid var(--color-gray-30)', backgroundColor: 'var(--color-gray-5)' }}
        >
          <h3
            className="flex items-center gap-2"
            style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 600, margin: '0 0 8px' }}
          >
            <Ban size={16} color="var(--color-gray-70)" />
            Annulé
            {issue.motifAnnulation ? ` — ${issue.motifAnnulation}` : ''}
            {issue.dateAnnulation ? ` · le ${formatJour(issue.dateAnnulation)}` : ''}
          </h3>
          <p
            style={{
              fontSize: 'var(--font-size-copy2)',
              lineHeight: '22px',
              color: 'var(--color-text-primary)',
              margin: 0,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {issue.commentaireCloture}
          </p>
          {blocReouverture}
        </section>
      )}
      {/* En édition, le formulaire prend la place de la description : elle
          y est, modifiable. Les photos, elles, ne s'éditent pas. */}
      {edition && (
        <EditionProbleme
          issue={issue}
          admin={estAdminAffiche}
          enCours={submitting}
          onEnregistrer={(saisie) => void enregistrerEdition(saisie)}
          onFermer={() => setEdition(false)}
          proposes={proposesMention}
        />
      )}
      {/* Description en grand, photos dedans : ce qu'on vient lire. */}
      <section className="px-4 pt-4" hidden={edition}>
        <div className="rounded-lg p-4 flex flex-col gap-3" style={{ backgroundColor: 'var(--color-gray-5)', position: 'relative' }}>
          {/* Coin haut droit : la pastille s'y étend vers la gauche. */}
          {champsTouches?.has('description') && (
            <span style={{ position: 'absolute', top: 6, right: 6 }}>{pastille('description', true)}</span>
          )}
          <p
            style={{
              fontSize: 16,
              lineHeight: '24px',
              margin: 0,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              color: issue.description ? 'var(--color-text-primary)' : 'var(--color-gray-70)',
              fontStyle: issue.description ? 'normal' : 'italic',
            }}
          >
            {issue.description ? <TexteAvecMentions texte={issue.description} /> : 'Pas de description.'}
            {iconeHistorique('description')}
          </p>
          {blocHistorique('description')}
          {pjLoading && (
            <span className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>
              <Loader2 size={14} className="animate-spin" /> Chargement des photos…
            </span>
          )}
          {/* Les photos, dans la description (déclaration puis activité). */}
          {!pjLoading && (photosFiche.length > 0 || issue.pieceJointe) && (
            <BlocPhotos photos={photosFiche} legacyUrl={issue.pieceJointe} onOuvrir={setRangVisionneuse} />
          )}
        </div>
      </section>
      {/* Masquée pendant l'annotation (ses raccourcis clavier gêneraient la
          saisie) ; elle revient sur la même photo, annotée. */}
      {rangVisionneuse !== null && !aAnnoter && (
        <Visionneuse
          photos={photosFiche}
          index={Math.min(rangVisionneuse, photosFiche.length - 1)}
          onIndex={setRangVisionneuse}
          onFermer={() => setRangVisionneuse(null)}
          onVoirDansActivite={voirDansActivite}
          {...(!termine ? { onAnnoter: (photo: PhotoFiche, affichee: PieceJointe) => setAAnnoter({ photo, affichee }) } : {})}
        />
      )}
      {aAnnoter && (
        <EditeurAnnotation
          source={
            aAnnoter.affichee.isPdf
              ? { pdf: () => lireContenu(aAnnoter.affichee) }
              : {
                  lire: () => lireContenu(aAnnoter.affichee),
                  adresses: [aAnnoter.affichee.apercu, aAnnoter.affichee.url].filter((u): u is string => !!u),
                }
          }
          mode="publier"
          enCours={envoiAnnotation}
          onAnnuler={() => setAAnnoter(null)}
          onTerminer={(image, texte) => void publierAnnotation(image, texte)}
        />
      )}

      {/* Bandeau qualification — deux issues : qualifier OU annuler.
          L'annulation vit ici et nulle part ailleurs : c'est au moment de
          qualifier que le coordinateur constate « ce n'est pas un problème »
          ou « c'est déjà réglé » (demande 3). */}
      {aQualifier && actionsOuvertes && (
        <div className="pt-4">
        <QualificationInviteCard
          metierLabel={issue.sujet ? `${METIERS_LABELS[issue.metier]} · ${issue.sujet}` : METIERS_LABELS[issue.metier]}
          familles={famillesDispo}
          qualifFamille={qualifFamille}
          setQualifFamille={setQualifFamille}
          qualifCriticite={qualifCriticite}
          setQualifCriticite={setQualifCriticite}
          aideCriticite={
            prioriteNcs
              ? `Proposée d'après la NC-S envoyée : priorité ${prioriteNcs} · ${PRIORITES.find((p) => p.valeur === prioriteNcs)?.libelle ?? ''}.`
              : undefined
          }
          onQualifier={() => void handleQualifier()}
          qualifying={qualifying}
          passeEnCours={issue.statut === 'Ouvert'}
          modeAnnulation={modeAnnulation}
          setModeAnnulation={setModeAnnulation}
          motifAnnulation={motifAnnulation}
          setMotifAnnulation={setMotifAnnulation}
          commentaireAnnulation={commentaireAnnulation}
          setCommentaireAnnulation={setCommentaireAnnulation}
          onAnnuler={() => void handleAnnuler()}
          proposes={proposesMention}
          caseGroupe={(libelle) => (
            <CaseGroupe
              nombre={nbAQualifier}
              coche={appliquerGroupe}
              onChange={setAppliquerGroupe}
              libelle={libelle}
              desactive={qualifying}
            />
          )}
        />
        </div>
      )}

      {!estBureau && <div className="px-4 pt-4 flex flex-col gap-3">{cartes}</div>}

      {/* ─── Timeline ─── */}
      <section className="px-4 pt-5 pb-4">
        <h3
          style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 500, marginBottom: 10 }}
        >
          Activité
        </h3>
        <Timeline
          commentaires={commentaires}
          pjParCommentaire={pjAttribution.parCommentaire}
          onOuvrirPhoto={ouvrirPhoto}
          peutCorriger={peutCorriger}
          onCorriger={corrigerCommentaire}
        />
      </section>

      {/* NC-S ouverte en volet (06/10) : la fiche, réduite, passe sur une colonne ;
          ses cartes viennent sous l'activité. */}
      {estBureau && !deuxColonnes && <div className="px-4 pb-4 flex flex-col gap-3">{cartes}</div>}

      {/* Zone commentaire + PJ — ouverte aussi sur un clos (26/10), fermée sur un annulé. */}
      {peutCommenter && (
        <section className="px-4 pb-4">
          <div
            style={{
              backgroundColor: 'var(--color-white)',
              border: '1px solid var(--color-border)',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <div className="flex items-end gap-2 p-2">
              <ZoneMentions
                ref={refZoneCommentaire}
                value={nouveauCommentaire}
                onValeur={setNouveauCommentaire}
                proposes={proposesMention}
                sansMentions={isFournisseur}
                // Une capture ou un fichier collé ici est joint au commentaire.
                onPaste={(e) => {
                  const colles = fichiersColles(e.clipboardData)
                  if (colles.length === 0 || submitting) return
                  e.preventDefault()
                  const { nouveaux, erreurs } = preparerFichiers(colles)
                  if (nouveaux.length > 0) {
                    setFichiersCommentaire((prev) => [...prev, ...nouveaux])
                    setShowPicker(true)
                  }
                  if (erreurs.length > 0) show(erreurs.join(' '), 'error')
                }}
                placeholder={isFournisseur ? 'Ajouter un commentaire…' : 'Ajouter un commentaire… (@ pour mentionner)'}
                rows={2}
                className="flex-1 resize-none outline-none border-none bg-transparent"
                style={{
                  fontSize: 'var(--font-size-copy2)',
                  lineHeight: '20px',
                  color: 'var(--color-text-primary)',
                }}
              />
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setShowPicker(!showPicker)}
                  aria-label="Ajouter des pièces jointes"
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: 36,
                    height: 36,
                    backgroundColor: showPicker ? 'var(--color-black)' : 'var(--color-gray-5)',
                    color: showPicker ? 'var(--color-white)' : 'var(--color-gray-60)',
                    border: 'none',
                    position: 'relative',
                  }}
                >
                  <Paperclip size={16} />
                  {fichiersCommentaire.length > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 16,
                        height: 16,
                        borderRadius: 8,
                        backgroundColor: 'var(--color-yellow)',
                        color: 'var(--color-black)',
                        fontSize: 10,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {fichiersCommentaire.length}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => void postCommentaire()}
                  disabled={
                    (!nouveauCommentaire.trim() && fichiersCommentaire.length === 0) ||
                    submitting
                  }
                  aria-label="Envoyer"
                  className="flex items-center justify-center rounded-full"
                  style={{
                    width: 36,
                    height: 36,
                    backgroundColor: 'var(--color-yellow)',
                    opacity:
                      (!nouveauCommentaire.trim() && fichiersCommentaire.length === 0) ||
                      submitting
                        ? 0.5
                        : 1,
                    border: 'none',
                  }}
                >
                  <Send size={16} />
                </button>
              </div>
            </div>

            {showPicker && (
              <div
                style={{
                  borderTop: '1px solid var(--color-border)',
                  padding: 12,
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    style={{
                      fontSize: 'var(--font-size-info)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 500,
                    }}
                  >
                    Ajouter des fichiers
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPicker(false)
                      setFichiersCommentaire([])
                    }}
                    style={{ background: 'none', border: 'none' }}
                  >
                    <X size={16} color="var(--color-gray-60)" />
                  </button>
                </div>
                <PieceJointePicker
                  fichiers={fichiersCommentaire}
                  onChange={setFichiersCommentaire}
                  disabled={submitting}
                />
              </div>
            )}
          </div>
          <CaseGroupe
            nombre={nbActifs}
            coche={commenterGroupe}
            onChange={setCommenterGroupe}
            libelle="Publier aussi sur"
            desactive={submitting}
          />
        </section>
      )}

      {sectionCloture}
        </div>
        {deuxColonnes && (
          <aside className="flex flex-col gap-3" style={{ paddingTop: 16 }}>
            {cartes}
          </aside>
        )}
      </div>
      {(fenetreFnct === 'escalade' || fenetreFnct === 'modele') && (
        <Suspense fallback={null}>
          <OuvertureFnct
            origine={{
              genre: 'probleme',
              issue,
              soeurs: membres,
              equipement,
              sansReleve: fenetreFnct === 'modele',
              onRelever: () => setFenetreFnct('releve'),
              onCreee: apresCreationFnct,
              onVoirFnct: (numero: string) => ouvrirFiche(lienFnct(numero)),
            }}
            auteur={nomReel || user?.nom || ''}
            auteurUPN={enVueRole || !user ? '' : user.upn.toLowerCase()}
            onClose={() => {
              setFenetreFnct(null)
              setVersionFnct((v) => v + 1)
            }}
          />
        </Suspense>
      )}
      {fenetreFnct === 'releve' && (
        <Suspense fallback={null}>
          <ReleveEcart
            origine={{ genre: 'probleme', issue, soeurs: membres, equipement, onSansReleve: () => setFenetreFnct('modele') }}
            onFini={(numero: string, nb: number) => {
              setFenetreFnct(null)
              setVersionFnct((v) => v + 1)
              show(`${nb > 1 ? `${nb} écarts ajoutés` : 'Écart ajouté'} au relevé ${numero}${FNCT_EN_ESSAI ? ' — essai : rien n’est écrit sur le problème' : ''}`, 'success')
            }}
            onClose={() => setFenetreFnct(null)}
          />
        </Suspense>
      )}
      {ncsOuverte && (
        <DeclarationNcs
          issue={issue}
          equipement={equipement}
          dejaDeclarees={declarationsNcs.map((d) => d.fichier)}
          onDeclaree={apresDeclarationNcs}
          onClose={() => setNcsOuverte(false)}
        />
      )}
      {envoiBEOuvert && (
        <EnvoiBE
          issue={issue}
          equipement={equipement}
          commentaires={commentaires}
          photos={photosFiche}
          avecPhoto={commentairesAvecPhoto}
          onEnvoye={async (contenu, message) => {
            await tracer({ problemId: id, type: 'Modification', contenu }, message)
            await refresh(true)
          }}
          onClose={() => setEnvoiBEOuvert(false)}
        />
      )}
    </Cadre>
  )
}

// ─── Cadres : page pleine ou surimpression ───

interface CadreProps {
  titre: string
  onBack: () => void
  /** Bouton à droite de la barre de titre (page pleine seulement). */
  action?: React.ReactNode
  children: React.ReactNode
}

function CadrePage({ titre, onBack, action, children }: CadreProps): JSX.Element {
  return (
    <AppShell header={<Header level={3} title={titre} showBack onBack={onBack} rightAction={action} />}>
      {children}
    </AppShell>
  )
}

/**
 * Sans AppShell : la croix de fermeture est fournie par ModaleRoute, et
 * l'identifiant du problème est dans l'en-tête de la fiche (v2).
 */
function CadreModale({ children }: CadreProps): JSX.Element {
  return <div>{children}</div>
}

// ─── Timeline avec PJ inline ───

function Timeline({
  commentaires,
  pjParCommentaire,
  onOuvrirPhoto,
  peutCorriger,
  onCorriger,
}: {
  commentaires: Commentaire[]
  pjParCommentaire: Map<string, PieceJointe[]>
  onOuvrirPhoto: (pj: PieceJointe) => void
  /** Ses propres commentaires, problème en cours (utils/editionCommentaire). */
  peutCorriger: (c: Commentaire) => boolean
  /** true : enregistré, la saisie se referme. */
  onCorriger: (c: Commentaire, texte: string) => Promise<boolean>
}): JSX.Element {
  const [enCorrection, setEnCorrection] = useState<string | null>(null)
  // Historique d'un commentaire corrigé (23/10) : ouvert sous lui, un à la fois.
  const [historiqueOuvert, setHistoriqueOuvert] = useState<string | null>(null)
  if (commentaires.length === 0) {
    return (
      <div
        className="text-center py-4 rounded"
        style={{
          backgroundColor: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-copy2)',
        }}
      >
        Aucune activité pour le moment
      </div>
    )
  }
  return (
    <ol className="space-y-3">
      {commentaires.map((c, idx) => {
        const isLast = idx === commentaires.length - 1
        const pjRattachees = pjParCommentaire.get(c.id) ?? []
        return (
          <li key={c.id} id={`evenement-${c.id}`} className="flex gap-3" style={{ scrollMarginTop: 80 }}>
            <div className="flex flex-col items-center">
              <PastilleEvenement type={c.type} contenu={c.contenu} auteur={c.auteur} upn={c.auteurEmail} />
              {!isLast && (
                <div
                  className="w-px flex-1"
                  style={{ backgroundColor: 'var(--color-border)', minHeight: 8 }}
                />
              )}
            </div>
            <div className="flex-1 pb-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-0.5">
                <span
                  style={{ fontSize: 'var(--font-size-copy2)', fontWeight: 500 }}
                >
                  {c.auteur || '—'}
                </span>
                {/* Date et icônes ensemble, jamais séparées par un retour à la ligne :
                    seul le nom peut passer au-dessus. */}
                <span className="inline-flex items-center" style={{ gap: 6, whiteSpace: 'nowrap' }}>
                <span
                  style={{
                    fontSize: 'var(--font-size-info)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {formatDate(c.date)}
                </span>
                {/* Corrigé (23/10) : une icône discrète ; l'heure en infobulle,
                    et dans l'historique qu'elle ouvre (au téléphone, pas de survol). */}
                {c.modifieLe && (
                  <button
                    type="button"
                    onClick={() => setHistoriqueOuvert((h) => (h === c.id ? null : c.id))}
                    aria-expanded={historiqueOuvert === c.id}
                    aria-label={`${libelleCorrection(c.date, c.modifieLe)} · voir l’historique`}
                    title={`${libelleCorrection(c.date, c.modifieLe)} · voir l’historique`}
                    className="inline-flex items-center justify-center commentaire-modifie"
                    data-ouvert={historiqueOuvert === c.id || undefined}
                  >
                    <History size={14} strokeWidth={2} />
                  </button>
                )}
                {peutCorriger(c) && enCorrection !== c.id && (
                  <button
                    type="button"
                    onClick={() => setEnCorrection(c.id)}
                    aria-label="Corriger mon commentaire"
                    title="Corriger mon commentaire"
                    className="inline-flex items-center justify-center commentaire-modifie"
                  >
                    <Pencil size={14} strokeWidth={2} />
                  </button>
                )}
                </span>
              </div>
              {enCorrection === c.id ? (
                <CorrectionCommentaire
                  commentaire={c}
                  onAnnuler={() => setEnCorrection(null)}
                  onEnregistrer={async (texte) => {
                    if (await onCorriger(c, texte)) setEnCorrection(null)
                  }}
                />
              ) : (
                <p
                  style={{
                    fontSize: 'var(--font-size-copy2)',
                    lineHeight: '20px',
                    color: 'var(--color-text-primary)',
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  <ContenuEvenement commentaire={c} />
                </p>
              )}

              {historiqueOuvert === c.id && c.itemId && <HistoriqueCommentaire key={c.modifieLe} itemId={c.itemId} />}

              {/* PJ rattachées inline sous le commentaire */}
              {pjRattachees.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <PieceJointeGallery
                    piecesJointes={pjRattachees}
                    onOuvrir={onOuvrirPhoto}
                    thumbSize={48}
                  />
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * Les textes successifs d'un commentaire corrigé (23/10), lus dans
 * l'historique des versions de SharePoint à l'ouverture : le plus récent
 * d'abord, chacun avec la date où il est apparu. Visible de tous.
 */
function HistoriqueCommentaire({ itemId }: { itemId: string }): JSX.Element {
  const [versions, setVersions] = useState<VersionCommentaire[] | null>(null)
  const [erreur, setErreur] = useState(false)
  useEffect(() => {
    let annule = false
    historiqueCommentaire(itemId)
      .then((v) => {
        if (!annule) setVersions(v)
      })
      .catch((err) => {
        console.warn('[IssueDetail] historique du commentaire illisible :', err)
        if (!annule) setErreur(true)
      })
    return () => {
      annule = true
    }
  }, [itemId])

  const cadre: CSSProperties = {
    marginTop: 8,
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: 'var(--color-gray-5)',
    fontSize: 'var(--font-size-info)',
    lineHeight: '18px',
  }
  if (erreur) return <p role="status" style={{ ...cadre, margin: '8px 0 0' }}>L’historique n’a pas pu être lu.</p>
  if (!versions) return <p role="status" style={{ ...cadre, margin: '8px 0 0', color: 'var(--color-text-secondary)' }}>Lecture de l’historique…</p>
  return <BlocHistorique textes={versions} />
}

/**
 * Saisie de la correction, à la place du texte (23/10). Ctrl + Entrée
 * enregistre, Échap annule (sans fermer la fiche dessous).
 */
function CorrectionCommentaire({
  commentaire,
  onAnnuler,
  onEnregistrer,
}: {
  commentaire: Commentaire
  onAnnuler: () => void
  onEnregistrer: (texte: string) => Promise<void>
}): JSX.Element {
  const [texte, setTexte] = useState(commentaire.contenu)
  const refZone = useHauteurAuto(texte)
  const [enCours, setEnCours] = useState(false)
  const erreur = erreurCorrection(commentaire.contenu, texte)
  const enregistrer = async (): Promise<void> => {
    if (erreur || enCours) return
    setEnCours(true)
    try {
      await onEnregistrer(texte)
    } finally {
      setEnCours(false)
    }
  }
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <ZoneMentions
        ref={refZone}
        id={'correction-' + commentaire.id}
        aria-label="Corriger le commentaire"
        value={texte}
        onValeur={setTexte}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            e.stopPropagation()
            onAnnuler()
          } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault()
            void enregistrer()
          }
        }}
        autoFocus
        disabled={enCours}
        rows={3}
        className="p-3 rounded-lg resize-none commentaire-correction"
        style={{ width: '100%', backgroundColor: 'var(--color-white)', fontSize: 'var(--font-size-copy2)', lineHeight: '20px', fontFamily: 'inherit' }}
      />
      <div className="flex items-center justify-end" style={{ gap: 4 }}>
        <Button variant="ghost" size="sm" onClick={onAnnuler} disabled={enCours} style={{ color: 'var(--color-gray-70)', fontWeight: 400 }}>
          Annuler
        </Button>
        <Button variant="primary" size="sm" onClick={() => void enregistrer()} disabled={!!erreur || enCours} iconLeft={enCours ? <Loader2 size={14} className="animate-spin" /> : undefined}>
          Enregistrer
        </Button>
      </div>
    </div>
  )
}

/**
 * Texte d'un événement. Une NC-S envoyée (06/10) se lit « NC-S envoyée à la
 * qualité », le fichier s'ouvrant par l'icône — plutôt que son nom et son
 * adresse en entier.
 */
function ContenuEvenement({ commentaire }: { commentaire: Commentaire }): JSX.Element {
  const ncs = commentaire.type === 'Modification' ? lireEvenementNcs(commentaire.contenu) : null
  if (!ncs) return <TexteAvecMentions texte={commentaire.contenu} />
  return (
    <span className="inline-flex items-center gap-1">
      {LIBELLE_EVENEMENT_NCS}
      {ncs.lien && (
        <a
          href={ncs.lien}
          target="_blank"
          rel="noreferrer"
          aria-label={`Ouvrir ${ncs.fichier} dans Excel`}
          title={`Ouvrir dans Excel — ${ncs.fichier}`}
          className="inline-flex items-center justify-center"
          style={{ width: 32, height: 32, margin: '-6px 0', borderRadius: 16, color: 'var(--color-black)' }}
        >
          <ExternalLink size={16} />
        </a>
      )}
    </span>
  )
}

// ─── Bandeaux criticité ───

// ─── QualificationInviteCard ───

/**
 * Bandeau du coordinateur sur un problème à qualifier. Deux issues :
 *
 *  - QUALIFIER (défaut) : famille + criticité. Fait passer Ouvert → En cours.
 *  - ANNULER : motif + commentaire, tous deux obligatoires. Le problème sort
 *    des vues actives mais reste consultable.
 *
 * L'annulation est volontairement en second plan (lien discret, pas un
 * bouton plein) : c'est l'exception. Un coordinateur qui qualifie à la chaîne
 * ne doit pas pouvoir annuler par un toucher malheureux — il faut basculer
 * de mode, puis choisir un motif, puis écrire.
 */
function QualificationInviteCard({
  metierLabel,
  familles,
  qualifFamille,
  setQualifFamille,
  qualifCriticite,
  setQualifCriticite,
  aideCriticite,
  onQualifier,
  qualifying,
  passeEnCours,
  modeAnnulation,
  setModeAnnulation,
  motifAnnulation,
  setMotifAnnulation,
  commentaireAnnulation,
  setCommentaireAnnulation,
  onAnnuler,
  caseGroupe,
  proposes,
}: {
  metierLabel: string
  familles: string[]
  qualifFamille: string
  setQualifFamille: (v: string) => void
  qualifCriticite: CriticiteTranchee | null
  setQualifCriticite: (v: CriticiteTranchee) => void
  /** Pourquoi une criticité est déjà cochée (NC-S envoyée, 06/10). */
  aideCriticite?: string
  onQualifier: () => void
  qualifying: boolean
  /** Le problème est `Ouvert` : la qualification le fera passer en cours. */
  passeEnCours: boolean
  modeAnnulation: boolean
  setModeAnnulation: (v: boolean) => void
  motifAnnulation: MotifAnnulation | null
  setMotifAnnulation: (v: MotifAnnulation) => void
  commentaireAnnulation: string
  setCommentaireAnnulation: (v: string) => void
  onAnnuler: () => void
  /** Lot 14 : « Qualifier aussi les autres machines du groupe ». */
  caseGroupe?: (libelle: string) => React.ReactNode
  /** Personnes de la fiche, proposées dès « @ » dans le commentaire (02/11). */
  proposes: readonly PersonneMentionnee[]
}): JSX.Element {
  const cadre = {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
  }
  const titre = {
    fontSize: 'var(--font-size-copy2)',
    fontWeight: 500,
    marginBottom: 12,
  }
  const etiquette = {
    fontSize: 'var(--font-size-info)',
    color: 'var(--color-text-secondary)',
    display: 'block',
    marginBottom: 6,
  } as const

  // ─── Mode annulation ───
  if (modeAnnulation) {
    const peutAnnuler =
      motifAnnulation !== null && commentaireAnnulation.trim() !== ''
    return (
      <section className="mx-4 mb-4 p-4 rounded-lg" style={cadre}>
        <h3 style={titre}>Annuler · {metierLabel}</h3>

        <div className="mb-3">
          <span style={etiquette}>Motif *</span>
          <GroupeBulles libelle="Motif d'annulation">
            {MOTIFS_ANNULATION.map((m) => (
              <Bulle
                key={m}
                role="radio"
                libelle={m}
                selectionne={motifAnnulation === m}
                onClick={() => setMotifAnnulation(m)}
                desactive={qualifying}
              />
            ))}
          </GroupeBulles>
        </div>

        <div className="mb-3">
          <label htmlFor="commentaire-annulation" style={etiquette}>
            Commentaire *
          </label>
          <ZoneMentions
            id="commentaire-annulation"
            value={commentaireAnnulation}
            onValeur={setCommentaireAnnulation}
            proposes={proposes}
            placeholder="Pourquoi ce problème est-il écarté ?"
            rows={3}
            aria-required="true"
            className="w-full p-3 rounded-lg resize-none outline-none"
            style={{
              fontSize: 'var(--font-size-copy2)',
              backgroundColor: 'var(--color-white)',
              border: '1px solid var(--color-border)',
            }}
          />
        </div>

        <p style={{ ...etiquette, marginBottom: 10 }}>
          Le problème sortira des listes actives. Il restera consultable, avec
          son motif, dans l&apos;historique.
        </p>
        {caseGroupe?.('Annuler aussi')}

        <Button
          variant="secondary"
          fullWidth
          onClick={onAnnuler}
          disabled={!peutAnnuler || qualifying}
          iconLeft={<Ban size={16} />}
        >
          {qualifying ? 'Annulation…' : 'Annuler le problème'}
        </Button>
        <Button
          variant="ghost"
          fullWidth
          onClick={() => setModeAnnulation(false)}
          disabled={qualifying}
          style={{ marginTop: 6 }}
        >
          Revenir à la qualification
        </Button>
      </section>
    )
  }

  // ─── Mode qualification (défaut) ───
  const canSubmit = qualifFamille !== '' && qualifCriticite !== null
  return (
    <section className="mx-4 mb-4 p-4 rounded-lg" style={cadre}>
      <h3 style={titre}>Qualifier · {metierLabel}</h3>

      {/* Familles en bulles, liste déroulante au-delà de onze (MECA) : même
          composant que le formulaire d'édition (Lot 6). */}
      <div className="mb-3">
        <ChoixFamille
          id="qualif-famille"
          familles={familles}
          valeur={qualifFamille}
          onChange={setQualifFamille}
          desactive={qualifying}
        />
      </div>

      <div className="mb-3">
        <ChoixCriticite valeur={qualifCriticite} onChange={setQualifCriticite} desactive={qualifying} />
        {aideCriticite && (
          <p style={{ margin: '6px 0 0', fontSize: 'var(--font-size-info)', color: 'var(--color-text-secondary)' }}>{aideCriticite}</p>
        )}
      </div>

      {/* Dit ce que la qualification va déclencher. Depuis le retrait de
          « Prise en charge », c'est le seul geste qui fait passer un
          problème en cours : le coordinateur doit le savoir avant, pas le
          découvrir au badge. */}
      {passeEnCours && (
        <p style={{ ...etiquette, marginBottom: 10 }}>
          Le problème passera en « En cours ».
        </p>
      )}
      {caseGroupe?.('Qualifier aussi')}

      <Button
        variant="primary"
        fullWidth
        onClick={onQualifier}
        disabled={!canSubmit || qualifying}
        iconLeft={<Tag size={16} />}
      >
        {qualifying ? 'Qualification…' : 'Qualifier le problème'}
      </Button>
      <Button
        variant="ghost"
        fullWidth
        onClick={() => setModeAnnulation(true)}
        disabled={qualifying}
        style={{ marginTop: 6 }}
      >
        Pas un problème ? L&apos;annuler
      </Button>
    </section>
  )
}

// ─── Étiquette de champ ───

/**
 * Libellé au-dessus d'un champ ou d'un groupe de bulles. Un `<label>` quand
 * il désigne un champ (`htmlFor`), un simple texte pour un groupe de bulles,
 * qui porte déjà son propre `aria-label`.
 */
/** Les déclarations NC-S déposées, d'après l'activité, de la plus ancienne à la plus récente. */
function declarationsNcsDe(
  commentaires: readonly Commentaire[],
): { fichier: string; lien: string; priorite?: PrioriteNcs; date: string; auteur: string }[] {
  return commentaires
    .flatMap((c) => {
      const lu = c.type === 'Modification' ? lireEvenementNcs(c.contenu) : null
      return lu ? [{ ...lu, date: c.date, auteur: c.auteur }] : []
    })
    .sort((a, b) => a.date.localeCompare(b.date))
}

function Etiquette({
  htmlFor,
  children,
}: {
  htmlFor?: string
  children: React.ReactNode
}): JSX.Element {
  const style = {
    fontSize: 'var(--font-size-info)',
    color: 'var(--color-text-secondary)',
    display: 'block',
    marginBottom: 6,
  } as const
  return htmlFor ? (
    <label htmlFor={htmlFor} style={style}>
      {children}
    </label>
  ) : (
    <span style={style}>{children}</span>
  )
}

// ─── Sous-composants ───


/** « 20 sept. 2026 » */
function formatJour(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** « le 12 sept. 2026 à 10:42 · il y a 12 jours » */
function quandDeclare(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  const jours = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  const depuis =
    jours <= 0 ? "aujourd'hui" : jours === 1 ? 'hier' : jours < 60 ? `il y a ${jours} jours` : `il y a ${Math.floor(jours / 30)} mois`
  return `le ${formatJour(iso)} à ${heure} · ${depuis}`
}

function formatDate(iso: string): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}
